import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUp,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  FileText,
  History,
  LocateFixed,
  Mail,
  MessageCircle,
  Plus,
  RefreshCcw,
  Sparkles,
  Square,
  X,
} from "lucide-react";
import { AIAssistantConfigProvider } from "../contexts/AIAssistantConfigContext";
import type {
  AssistantActionBase,
  AIAssistantComposerChestSection,
  AIAssistantWorkspaceState,
  AIAssistantWorkspaceTabId,
  AssistantBranding,
  AssistantConversationState,
  AssistantLayout,
  AssistantPrompt,
  AssistantPromptProvenance,
  AssistantStrings,
  AssistantInputAction,
  AssistantContextSummaryState,
  AssistantActiveRouteContextEnvelope,
  AssistantOpenArtifactInGoogleSheetsHandler,
  AssistantPolicyEventHandler,
} from "../types";
import { useAssistantPanel } from "../hooks/useAssistantPanel";
import type { AssistantPanelState } from "../hooks/useAssistantPanel";
import { AIAssistantHeader } from "./AIAssistantHeader";
import type { AIAssistantHeaderProps } from "./AIAssistantHeader";
import { AIAssistantContextSummary } from "./AIAssistantContextSummary";
import type { AIAssistantContextSummaryProps } from "./AIAssistantContextSummary";
import { AIAssistantMessage } from "./AIAssistantMessage";
import type { AIAssistantMessageProps } from "./AIAssistantMessage";
import { AIAssistantSuggestedPrompts } from "./AIAssistantSuggestedPrompts";
import type { AIAssistantSuggestedPromptsProps } from "./AIAssistantSuggestedPrompts";
import { AIAssistantThinkingIndicator } from "./AIAssistantThinkingIndicator";
import { AIAssistantSchoolShellContextMenuContent } from "./AIAssistantSchoolShellContextMenuContent";
import styles from "../styles/AIAssistant.module.css";

import {
  ActionIcon,
  Button,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "../ui/primitives.js";

import { DesignSystemBoundary } from "../ui/DesignSystemProvider.js";
import { AssistantAnimatedIcon } from "./internal/AssistantAnimatedIcon";
import { schoolShellComposerStyleContract } from "./internal/schoolShellComposerStyleContract";
import { schoolShellAssistantStyleContract } from "./internal/schoolShellAssistantStyleContract";
import { AssistantMeasuredDisclosure } from "./internal/AssistantMeasuredDisclosure";
import {
  getAssistantMotionTransition,
  getAssistantTransitionStyle,
  resolveAssistantSpring,
} from "./internal/assistantMotion";
import {
  ASSISTANT_SHELL_STATE_EVENT,
  type AssistantShellStateDetail,
} from "./assistantShellEvents";

export type AIAssistantComponentOverrides<
  TCitation,
  TAction extends AssistantActionBase,
> = {
  Header: React.ComponentType<AIAssistantHeaderProps>;
  ContextSummary: React.ComponentType<AIAssistantContextSummaryProps>;
  Message: React.ComponentType<AIAssistantMessageProps<TCitation, TAction>>;
  SuggestedPrompts: React.ComponentType<AIAssistantSuggestedPromptsProps>;
  ThinkingIndicator: React.ComponentType;
};

export type AIAssistantRecentItem = {
  id: string;
  title: string;
  subtitle: string;
  prompt?: string;
  provenance?: AssistantPromptProvenance;
  sessionId?: string;
  previewMessageId?: string;
  source?: "persisted-session" | "current-session" | "prompt-template";
};

export type AIAssistantAttachmentChip = {
  id: string;
  label: string;
  detail?: string;
  tone?: "neutral" | "success" | "warning" | "error";
};

export type AIAssistantInputBanner = {
  kind?: "info" | "warning" | "error" | "rate-limit";
  title: string;
  description?: string;
  primaryActionLabel?: string;
  onPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
};

export type AIAssistantToastNotification = {
  message: string;
  tone?: "success" | "info" | "warning" | "error";
};

export type AIAssistantProps<TCitation, TAction extends AssistantActionBase> = {
  branding: AssistantBranding;
  strings?: AssistantStrings;
  metaLine?: string;
  prompts?: AssistantPrompt[] | string[];
  suggestedTitle?: string;
  onPromptClick?: (prompt: AssistantPrompt) => void;
  conversation: AssistantConversationState<TCitation, TAction>;
  inputActions?: AssistantInputAction[];
  onInputAction?: (action: AssistantInputAction, value?: string) => void;
  onCitationClick?: (citation: TCitation) => void;
  citationRenderer?: (citation: TCitation) => React.ReactNode;
  citationTitle?: string;
  citationDisplayMode?: "modal" | "inline_sources";
  contextSummary?: AssistantContextSummaryState;
  layout?: AssistantLayout;
  defaultExpanded?: boolean;
  className?: string;
  panelState?: AssistantPanelState;
  /** Optional close affordance callback for embedded/sidebar shell integrations. */
  onRequestClose?: () => void;
  /** Optional file-attachment callback for school-shell style chat inputs. */
  onAttachFile?: (file: File) => void | Promise<void>;
  /** Slot for additional controls rendered inside the header (right-side area). */
  headerActions?: React.ReactNode;
  /** Optional content rendered directly above the composer. */
  composerAccessory?: React.ReactNode;
  /** Optional content rendered above the chat transcript inside the conversation body. */
  conversationLeadContent?: React.ReactNode;
  /** Optional package-owned workspace panels for artifacts/history/navigation. */
  assistantWorkspace?: AIAssistantWorkspaceState;
  /** Visual variant for sidebar shell integrations. */
  variant?: "default" | "school-shell";
  /**
   * Optional controlled school-shell workspace tab state.
   * Controlled chat stays open while empty persisted threads hydrate.
   */
  schoolShellView?: AIAssistantWorkspaceTabId;
  /** Optional callback fired when the school-shell workspace tab changes. */
  onSchoolShellViewChange?: (tabId: AIAssistantWorkspaceTabId) => void;
  /** Optional recent activity rows shown in school-shell dashboard mode. */
  recentItems?: readonly AIAssistantRecentItem[];
  /** Optional callback fired when a recent item is activated. */
  onRecentItemClick?: (item: AIAssistantRecentItem) => void;
  /** Optional attachment chips rendered above the school-shell composer. */
  stagedAttachments?: readonly AIAssistantAttachmentChip[];
  /** Optional status banner rendered above the composer/input lane. */
  inputBanner?: AIAssistantInputBanner | null;
  /** Optional toast rendered outside the panel body for transient confirmation/error feedback. */
  toastNotification?: AIAssistantToastNotification | null;
  /** Optional compact model label rendered directly in the school-shell header. */
  schoolShellModelLabel?: string;
  /** Opaque active-route envelope projected by the consuming route adapter. */
  activeRouteContext?: AssistantActiveRouteContextEnvelope | null;
  /** Optional app-owned policy seam for route-local and artifact actions. */
  onAssistantPolicyEvent?: AssistantPolicyEventHandler;
  /** Optional app-owned artifact export bridge callback for Google Workspace actions. */
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler;
  /** Hide the built-in school-shell header when an outer shell owns the prototype chrome. */
  showSchoolShellHeader?: boolean;
  components?: Partial<AIAssistantComponentOverrides<TCitation, TAction>>;
};

type StagedAssistantAttachment = {
  id: string;
  fileName: string;
  sizeBytes: number;
};

const normalizePrompts = (
  prompts: AssistantPrompt[] | string[] | undefined,
): AssistantPrompt[] => {
  if (!prompts) {
    return [];
  }
  if (prompts.length === 0) {
    return [];
  }
  if (typeof prompts[0] === "string") {
    return (prompts as string[]).map((label) => ({
      id: label,
      label,
    }));
  }
  return prompts as AssistantPrompt[];
};

const resolveAssistantPromptText = (prompt: AssistantPrompt): string => {
  const text = prompt.prompt?.trim();
  return text && text.length > 0 ? text : prompt.label;
};

const joinAttributeList = (
  values: string[] | undefined,
): string | undefined => {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined;
  }
  const filtered = values
    .map((value) => String(value ?? "").trim())
    .filter((value) => value.length > 0);
  return filtered.length > 0 ? filtered.join(",") : undefined;
};

const resolveQuickActionIcon = (
  prompt: AssistantPrompt,
): React.ComponentType<{
  size?: number | string;
  strokeWidth?: number | string;
}> => {
  if (prompt.icon) {
    return prompt.icon;
  }
  const text = prompt.label.toLowerCase();
  if (text.includes("deadline") || text.includes("interview")) {
    return Calendar;
  }
  if (text.includes("document") || text.includes("upload")) {
    return FileText;
  }
  if (
    text.includes("email") ||
    text.includes("message") ||
    text.includes("draft")
  ) {
    return Mail;
  }
  if (text.includes("score") || text.includes("analy")) {
    return BarChart3;
  }
  return ClipboardList;
};

const SCHOOL_SHELL_CONSTRAINED_BREAKPOINT = 1024;
const ROUTE_START_POINTER_INTERLOCK_MS = 360;
const SCHOOL_SHELL_TRANSCRIPT_BOTTOM_THRESHOLD_PX = 80;

const isTranscriptNearBottom = (
  element: Pick<HTMLElement, "scrollHeight" | "scrollTop" | "clientHeight">,
  thresholdPx = SCHOOL_SHELL_TRANSCRIPT_BOTTOM_THRESHOLD_PX,
): boolean =>
  element.scrollHeight - element.scrollTop - element.clientHeight <=
  thresholdPx;

const buildSchoolShellTranscriptTailSignature = (
  messages: ReadonlyArray<{
    role: string;
    messageId?: string | null;
    metadata?: unknown;
  }>,
): string =>
  messages
    .slice(-2)
    .map((message, index) => {
      const metadata = message.metadata;
      const taskThread =
        metadata &&
        typeof metadata === "object" &&
        !Array.isArray(metadata) &&
        typeof (metadata as { taskThread?: unknown }).taskThread === "object" &&
        !Array.isArray((metadata as { taskThread?: unknown }).taskThread)
          ? (metadata as { taskThread?: Record<string, unknown> }).taskThread
          : null;
      const taskThreadSignature = taskThread
        ? [
            taskThread.threadId,
            taskThread.state,
            taskThread.stateLabel,
            taskThread.statusMessage,
            taskThread.progressPercent,
          ]
            .map((value) => String(value ?? "").trim())
            .join(":")
        : "";
      const messageId = String(
        message.messageId ?? `tail-${messages.length - 2 + index}`,
      ).trim();
      return [message.role, messageId, taskThreadSignature]
        .filter(Boolean)
        .join(":");
    })
    .join("|");

export function AIAssistant<TCitation, TAction extends AssistantActionBase>({
  branding,
  strings,
  metaLine,
  prompts,
  suggestedTitle,
  onPromptClick,
  conversation,
  inputActions,
  onInputAction,
  onCitationClick,
  citationRenderer,
  citationTitle = "Source Detail",
  citationDisplayMode = "modal",
  contextSummary,
  layout = "overlay",
  defaultExpanded = false,
  className,
  panelState,
  onRequestClose,
  onAttachFile,
  headerActions,
  composerAccessory,
  conversationLeadContent,
  assistantWorkspace,
  variant = "default",
  schoolShellView: controlledSchoolShellView,
  onSchoolShellViewChange,
  recentItems,
  onRecentItemClick,
  stagedAttachments,
  inputBanner,
  toastNotification,
  schoolShellModelLabel,
  activeRouteContext,
  onAssistantPolicyEvent,
  onOpenArtifactInGoogleSheets,
  showSchoolShellHeader = true,
  components,
}: AIAssistantProps<TCitation, TAction>) {
  const [question, setQuestion] = useState("");
  const [stagedFileAttachments, setStagedFileAttachments] = useState<
    StagedAssistantAttachment[]
  >([]);
  const [activeInputActionId, setActiveInputActionId] = useState<string | null>(
    null,
  );
  const [inputActionValue, setInputActionValue] = useState("");
  const [activeCitation, setActiveCitation] = useState<TCitation | null>(null);
  const [isAttachingFile, setIsAttachingFile] = useState(false);
  const [isComposingInput, setIsComposingInput] = useState(false);
  const [isConstrainedSchoolShell, setIsConstrainedSchoolShell] =
    useState(false);
  const [showAccessorySheet, setShowAccessorySheet] = useState(false);
  const [schoolShellGreetingVisible, setSchoolShellGreetingVisible] =
    useState(false);
  const [schoolShellGreetingDismissed, setSchoolShellGreetingDismissed] =
    useState(false);
  const [schoolShellViewportStyle, setSchoolShellViewportStyle] = useState<
    CSSProperties | undefined
  >(undefined);
  const [
    routeStartPointerInterlockActive,
    setRouteStartPointerInterlockActive,
  ] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const schoolShellChatAreaRef = useRef<HTMLDivElement>(null);
  const routeStartPointerInterlockUntilRef = useRef(0);
  const routeStartPointerInterlockTimerRef = useRef<number | null>(null);
  const routeStartEmptyStateVisibleRef = useRef(false);
  const schoolShellTranscriptSnapshotRef = useRef<{
    messageCount: number;
    pendingQuestion: string;
    tailSignature: string;
  } | null>(null);
  const reducedMotion = Boolean(useReducedMotion());

  const microTransitionStyle = useMemo(
    () => getAssistantTransitionStyle("micro", reducedMotion),
    [reducedMotion],
  );
  const panelTransitionStyle = useMemo(
    () =>
      getAssistantTransitionStyle("panel", reducedMotion, [
        "transform",
        "opacity",
        "box-shadow",
        "background-color",
        "border-color",
      ]),
    [reducedMotion],
  );
  const subtleLiftHover = reducedMotion ? undefined : { y: -2 };
  const subtlePressMotion = reducedMotion ? undefined : { scale: 0.98 };

  const internalPanelState = useAssistantPanel({ layout, defaultExpanded });
  const panel = panelState ?? internalPanelState;
  const isOverlay = panel.isOverlay;

  const promptItems = useMemo(() => normalizePrompts(prompts), [prompts]);
  const quickActionItems = useMemo(
    () => promptItems.slice(0, 5),
    [promptItems],
  );
  const resolvedSuggestedTitle =
    suggestedTitle ?? strings?.suggestedTitle ?? "Suggested prompts";
  const resolvedInputActionsTitle =
    strings?.inputActionsTitle ?? "Quick actions";
  const actionItems = useMemo(
    () => (inputActions ?? []).filter(Boolean),
    [inputActions],
  );
  const activeInputAction = useMemo(
    () =>
      actionItems.find((action) => action.id === activeInputActionId) ?? null,
    [actionItems, activeInputActionId],
  );

  const hasConversation = conversation.messages.length > 0;
  const isSchoolShell = variant === "school-shell";
  const [uncontrolledSchoolShellView, setUncontrolledSchoolShellView] =
    useState<AIAssistantWorkspaceTabId>(() =>
      conversation.messages.length > 0
        ? "chat"
        : (assistantWorkspace?.defaultTab ?? "navigate"),
    );
  const pendingSchoolShellTranscriptRevealRef = useRef(false);
  const schoolShellFollowDownUntilRef = useRef(0);
  const schoolShellTranscriptPinnedToBottomRef = useRef(true);
  const schoolShellView =
    controlledSchoolShellView ?? uncontrolledSchoolShellView;
  const schoolShellArtifactsAvailable = Boolean(assistantWorkspace?.artifacts);
  const setSchoolShellView = useCallback(
    (
      nextValue:
        | AIAssistantWorkspaceTabId
        | ((current: AIAssistantWorkspaceTabId) => AIAssistantWorkspaceTabId),
    ) => {
      const resolved =
        typeof nextValue === "function"
          ? nextValue(controlledSchoolShellView ?? uncontrolledSchoolShellView)
          : nextValue;
      if (controlledSchoolShellView === undefined) {
        setUncontrolledSchoolShellView(resolved);
      }
      onSchoolShellViewChange?.(resolved);
    },
    [
      controlledSchoolShellView,
      onSchoolShellViewChange,
      uncontrolledSchoolShellView,
    ],
  );
  const followSchoolShellTranscriptToBottom = useCallback((durationMs = 0) => {
    const chatArea = schoolShellChatAreaRef.current;
    if (!chatArea) {
      return;
    }
    schoolShellTranscriptPinnedToBottomRef.current = true;
    if (durationMs > 0) {
      schoolShellFollowDownUntilRef.current = Math.max(
        schoolShellFollowDownUntilRef.current,
        Date.now() + durationMs,
      );
    }
    const followTranscriptToBottom = () => {
      if (typeof chatArea.scrollTo === "function") {
        chatArea.scrollTo({
          top: chatArea.scrollHeight,
          behavior: "auto",
        });
      } else {
        chatArea.scrollTop = chatArea.scrollHeight;
      }
    };
    followTranscriptToBottom();
    if (typeof window === "undefined") {
      return;
    }
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(followTranscriptToBottom);
    }
    [40, 120, 360, 700, 1_000, 1_150, 1_300, 1_800, 2_500].forEach(
      (delayMs) => {
        window.setTimeout(followTranscriptToBottom, delayMs);
      },
    );
  }, []);
  const handleSchoolShellTranscriptScroll = useCallback(() => {
    const chatArea = schoolShellChatAreaRef.current;
    if (!chatArea) {
      return;
    }
    const pinnedToBottom = isTranscriptNearBottom(chatArea);
    schoolShellTranscriptPinnedToBottomRef.current = pinnedToBottom;
    if (!pinnedToBottom) {
      schoolShellFollowDownUntilRef.current = 0;
    }
  }, []);
  const scrollAssistantTranscriptToBottom = useCallback(() => {
    if (isSchoolShell) {
      followSchoolShellTranscriptToBottom(2_500);
      return;
    }
    bottomRef.current?.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "end",
    });
  }, [followSchoolShellTranscriptToBottom, isSchoolShell, reducedMotion]);
  const armRouteStartPointerInterlock = useCallback(
    (durationMs = ROUTE_START_POINTER_INTERLOCK_MS) => {
      if (!isSchoolShell || typeof window === "undefined") {
        return;
      }
      const nextUntil = Date.now() + durationMs;
      routeStartPointerInterlockUntilRef.current = Math.max(
        routeStartPointerInterlockUntilRef.current,
        nextUntil,
      );
      setRouteStartPointerInterlockActive(true);
      if (routeStartPointerInterlockTimerRef.current !== null) {
        window.clearTimeout(routeStartPointerInterlockTimerRef.current);
      }
      routeStartPointerInterlockTimerRef.current = window.setTimeout(
        () => {
          routeStartPointerInterlockTimerRef.current = null;
          if (Date.now() >= routeStartPointerInterlockUntilRef.current) {
            setRouteStartPointerInterlockActive(false);
          }
        },
        Math.max(0, routeStartPointerInterlockUntilRef.current - Date.now()),
      );
    },
    [isSchoolShell],
  );
  const isRouteStartPointerInterlockBlockingEvent = useCallback(
    (
      prompt: AssistantPrompt,
      event?: React.MouseEvent<HTMLElement>,
    ): boolean => {
      if (!isSchoolShell || prompt.interactionMode !== "chat-route-start") {
        return false;
      }
      if ((event?.detail ?? 0) <= 0) {
        return false;
      }
      return Date.now() < routeStartPointerInterlockUntilRef.current;
    },
    [isSchoolShell],
  );
  const resolvedRecentItems = useMemo<AIAssistantRecentItem[]>(
    () =>
      recentItems && recentItems.length > 0
        ? [...recentItems]
        : quickActionItems.slice(0, 3).map((prompt, index) => ({
            id: prompt.id ?? `recent-quick-action-${index + 1}`,
            title: prompt.label,
            subtitle: "Route-scoped quick action",
            prompt: resolveAssistantPromptText(prompt),
            provenance: prompt.provenance,
            source: "prompt-template",
          })),
    [quickActionItems, recentItems],
  );
  const resolvedAttachmentChips = useMemo(
    () => stagedAttachments?.filter(Boolean) ?? [],
    [stagedAttachments],
  );
  const toastToneClass =
    toastNotification?.tone === "error"
      ? "border-red-200 bg-red-800 text-white"
      : toastNotification?.tone === "warning"
        ? "border-orange-200 bg-orange-800 text-white"
        : toastNotification?.tone === "info"
          ? "border-primary/12 bg-irongrey text-white"
          : "border-primary/12 bg-irongrey text-white";
  const isInputLocked = inputBanner?.kind === "rate-limit";

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    return () => {
      if (routeStartPointerInterlockTimerRef.current !== null) {
        window.clearTimeout(routeStartPointerInterlockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSchoolShell || typeof window === "undefined") {
      return undefined;
    }
    const handleShellState = (event: Event) => {
      const detail = (event as CustomEvent<AssistantShellStateDetail>).detail;
      if (detail?.expanded) {
        armRouteStartPointerInterlock();
      }
    };
    window.addEventListener(
      ASSISTANT_SHELL_STATE_EVENT,
      handleShellState as EventListener,
    );
    return () => {
      window.removeEventListener(
        ASSISTANT_SHELL_STATE_EVENT,
        handleShellState as EventListener,
      );
    };
  }, [armRouteStartPointerInterlock, isSchoolShell]);

  useEffect(() => {
    if (!isSchoolShell) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }
    const media = window.matchMedia(
      `(max-width: ${SCHOOL_SHELL_CONSTRAINED_BREAKPOINT}px)`,
    );
    const update = () => setIsConstrainedSchoolShell(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => {
      media.removeEventListener?.("change", update);
    };
  }, [isSchoolShell]);

  useEffect(() => {
    if (
      !isSchoolShell ||
      !isConstrainedSchoolShell ||
      typeof window === "undefined"
    ) {
      setSchoolShellViewportStyle(undefined);
      return;
    }
    const applyViewportMetrics = () => {
      const viewport = window.visualViewport;
      const viewportHeight = Math.round(viewport?.height ?? window.innerHeight);
      const viewportOffsetTop = Math.max(
        0,
        Math.round(viewport?.offsetTop ?? 0),
      );
      const keyboardInset = Math.max(
        0,
        Math.round(
          window.innerHeight -
            ((viewport?.height ?? window.innerHeight) +
              (viewport?.offsetTop ?? 0)),
        ),
      );
      setSchoolShellViewportStyle({
        height: `${viewportHeight}px`,
        maxHeight: `${viewportHeight}px`,
        paddingTop:
          viewportOffsetTop > 0 ? `${viewportOffsetTop}px` : undefined,
        ["--ai-school-shell-keyboard-inset" as "--ai-school-shell-keyboard-inset"]: `${keyboardInset}px`,
      } as CSSProperties);
    };
    applyViewportMetrics();
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", applyViewportMetrics);
    viewport?.addEventListener("scroll", applyViewportMetrics);
    window.addEventListener("resize", applyViewportMetrics);
    return () => {
      viewport?.removeEventListener("resize", applyViewportMetrics);
      viewport?.removeEventListener("scroll", applyViewportMetrics);
      window.removeEventListener("resize", applyViewportMetrics);
    };
  }, [isConstrainedSchoolShell, isSchoolShell]);

  useEffect(() => {
    if (!isSchoolShell) {
      return;
    }
    if (schoolShellView === "artifacts" && !schoolShellArtifactsAvailable) {
      setSchoolShellView("chat");
      return;
    }
    if (assistantWorkspace?.workspace) {
      setSchoolShellView("workspace");
      return;
    }
    setSchoolShellView((current) =>
      current === "workspace"
        ? (assistantWorkspace?.defaultTab ?? "navigate")
        : current,
    );
  }, [
    assistantWorkspace?.defaultTab,
    assistantWorkspace?.workspace,
    isSchoolShell,
    schoolShellArtifactsAvailable,
    schoolShellView,
    setSchoolShellView,
  ]);

  useEffect(() => {
    if (isSchoolShell) {
      const nextSnapshot = {
        messageCount: conversation.messages.length,
        pendingQuestion: String(conversation.pendingQuestion ?? "").trim(),
        tailSignature: buildSchoolShellTranscriptTailSignature(
          conversation.messages,
        ),
      };
      const previousSnapshot = schoolShellTranscriptSnapshotRef.current;
      schoolShellTranscriptSnapshotRef.current = nextSnapshot;
      if (!previousSnapshot) {
        return;
      }
      const promptSubmissionStarted =
        nextSnapshot.pendingQuestion.length > 0 &&
        nextSnapshot.pendingQuestion !== previousSnapshot.pendingQuestion;
      const assistantReplyAppended =
        nextSnapshot.messageCount > previousSnapshot.messageCount &&
        previousSnapshot.pendingQuestion.length > 0;
      const transcriptMessageAppended =
        nextSnapshot.messageCount > previousSnapshot.messageCount &&
        nextSnapshot.tailSignature !== previousSnapshot.tailSignature;
      const transcriptTailUpdated =
        nextSnapshot.messageCount === previousSnapshot.messageCount &&
        nextSnapshot.messageCount > 0 &&
        nextSnapshot.tailSignature !== previousSnapshot.tailSignature;
      if (
        !promptSubmissionStarted &&
        !assistantReplyAppended &&
        !transcriptMessageAppended &&
        !transcriptTailUpdated
      ) {
        return;
      }
      followSchoolShellTranscriptToBottom(2_500);
      return;
    }
    if (!bottomRef.current) {
      return;
    }
    bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [
    conversation.messages,
    conversation.pendingQuestion,
    followSchoolShellTranscriptToBottom,
    isSchoolShell,
  ]);

  useEffect(() => {
    if (!isSchoolShell || typeof ResizeObserver === "undefined") {
      return;
    }
    const chatArea = schoolShellChatAreaRef.current;
    if (!chatArea) {
      return;
    }
    const activePanel = chatArea.querySelector(
      '[data-testid="ai-school-shell-active-panel"]',
    );
    const followIfArmed = () => {
      if (Date.now() > schoolShellFollowDownUntilRef.current) {
        return;
      }
      if (!schoolShellTranscriptPinnedToBottomRef.current) {
        return;
      }
      if (typeof chatArea.scrollTo === "function") {
        chatArea.scrollTo({
          top: chatArea.scrollHeight,
          behavior: "auto",
        });
      } else {
        chatArea.scrollTop = chatArea.scrollHeight;
      }
    };
    const observer = new ResizeObserver(followIfArmed);
    observer.observe(chatArea);
    if (activePanel instanceof Element) {
      observer.observe(activePanel);
    }
    return () => {
      observer.disconnect();
    };
  }, [isSchoolShell]);

  useEffect(() => {
    if (!activeInputAction) {
      setInputActionValue("");
      return;
    }
    const defaultValue =
      activeInputAction.input?.defaultValue ??
      activeInputAction.input?.options?.[0]?.value ??
      "";
    setInputActionValue(defaultValue);
  }, [activeInputAction]);

  const handleSubmit = useCallback(
    (value: string): boolean => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return false;
      }
      const attachmentContext =
        stagedFileAttachments.length > 0
          ? `\n\nAttached files:\n${stagedFileAttachments.map((attachment) => `- ${attachment.fileName}`).join("\n")}`
          : "";
      const didSubmit = conversation.submitQuestion(
        `${trimmedValue}${attachmentContext}`,
      );
      if (didSubmit) {
        setQuestion("");
        setStagedFileAttachments([]);
        setShowAccessorySheet(false);
      }
      return didSubmit;
    },
    [conversation, stagedFileAttachments],
  );

  const submitSchoolShellQuestion = useCallback(
    (value: string): boolean => {
      if (!value.trim()) {
        return false;
      }
      pendingSchoolShellTranscriptRevealRef.current = true;
      setSchoolShellView("chat");
      const didSubmit = handleSubmit(value);
      if (!didSubmit) {
        pendingSchoolShellTranscriptRevealRef.current = false;
      }
      return didSubmit;
    },
    [handleSubmit, setSchoolShellView],
  );

  const findTaskProposalAction = useCallback(
    (proposalId: string): TAction | null => {
      for (
        let index = conversation.messages.length - 1;
        index >= 0;
        index -= 1
      ) {
        const message = conversation.messages[index];
        const action = message.actions?.find(
          (candidate) => candidate.id === proposalId,
        );
        if (action) {
          return action;
        }
      }
      return null;
    },
    [conversation.messages],
  );

  const findTaskProposalQuestion = useCallback(
    (proposalId: string): string | null => {
      for (
        let index = conversation.messages.length - 1;
        index >= 0;
        index -= 1
      ) {
        const sections =
          conversation.messages[index].presentation?.sections ?? [];
        for (const section of sections) {
          if (
            section.type === "task_proposal" &&
            section.proposal.proposalId === proposalId
          ) {
            const question = section.proposal.question?.trim();
            return question && question.length > 0 ? question : null;
          }
        }
      }
      return null;
    },
    [conversation.messages],
  );

  const handleTaskProposalStart = useCallback(
    (proposalId: string): void => {
      const action = findTaskProposalAction(proposalId);
      if (!action) {
        return;
      }
      conversation.handleAction(action);
    },
    [conversation, findTaskProposalAction],
  );

  const handleTaskProposalAskInChat = useCallback(
    (proposalId: string): void => {
      const action = findTaskProposalAction(proposalId);
      const actionPrompt =
        typeof action?.payload?.prompt === "string"
          ? action.payload.prompt.trim()
          : "";
      const proposalQuestion = findTaskProposalQuestion(proposalId);
      const prompt =
        actionPrompt.length > 0 ? actionPrompt : (proposalQuestion ?? "");
      if (!prompt) {
        return;
      }
      submitSchoolShellQuestion(prompt);
    },
    [
      findTaskProposalAction,
      findTaskProposalQuestion,
      submitSchoolShellQuestion,
    ],
  );

  const handleRecentItemClick = useCallback(
    (item: AIAssistantRecentItem): void => {
      if (onRecentItemClick) {
        onRecentItemClick(item);
        return;
      }
      submitSchoolShellQuestion(item.prompt ?? item.title);
    },
    [onRecentItemClick, submitSchoolShellQuestion],
  );

  const handleAttachInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !onAttachFile) {
        return;
      }
      try {
        setIsAttachingFile(true);
        await onAttachFile(file);
        setStagedFileAttachments((current) => [
          ...current,
          {
            id: `${file.name}-${file.size}-${Date.now()}`,
            fileName: file.name,
            sizeBytes: file.size,
          },
        ]);
      } catch (error) {
        console.warn("[AI Assistant] Attachment handling failed", error);
      } finally {
        setIsAttachingFile(false);
      }
    },
    [onAttachFile],
  );

  const removeStagedAttachment = useCallback((attachmentId: string) => {
    setStagedFileAttachments((current) =>
      current.filter((attachment) => attachment.id !== attachmentId),
    );
  }, []);

  const triggerAttachInput = useCallback(() => {
    if (conversation.isLoading || isAttachingFile) {
      return;
    }
    attachInputRef.current?.click();
  }, [conversation.isLoading, isAttachingFile]);

  const handlePromptSelection = useCallback(
    (prompt: AssistantPrompt, event?: React.MouseEvent<HTMLElement>) => {
      if (!prompt) {
        return;
      }
      if (isRouteStartPointerInterlockBlockingEvent(prompt, event)) {
        event?.preventDefault();
        event?.stopPropagation();
        return;
      }
      if (isSchoolShell) {
        setSchoolShellView("chat");
      }
      const promptText = resolveAssistantPromptText(prompt);
      onPromptClick?.(prompt);
      if (!onPromptClick) {
        if (isSchoolShell) {
          submitSchoolShellQuestion(promptText);
          return;
        }
        handleSubmit(promptText);
      }
    },
    [
      handleSubmit,
      isRouteStartPointerInterlockBlockingEvent,
      isSchoolShell,
      onPromptClick,
      submitSchoolShellQuestion,
    ],
  );

  const handleTextareaKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if ((event.nativeEvent as KeyboardEvent).isComposing || isComposingInput) {
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (isSchoolShell) {
        submitSchoolShellQuestion(question);
        return;
      }
      handleSubmit(question);
    }
  };

  const handleNewConversation = useCallback(() => {
    if (isSchoolShell) {
      armRouteStartPointerInterlock();
    }
    if (isSchoolShell) {
      setSchoolShellView(
        conversationLeadContent
          ? "chat"
          : (assistantWorkspace?.defaultTab ?? "navigate"),
      );
    }
    conversation.resetConversation();
    setQuestion("");
    setStagedFileAttachments([]);
    setShowAccessorySheet(false);
  }, [
    armRouteStartPointerInterlock,
    assistantWorkspace?.defaultTab,
    conversation,
    conversationLeadContent,
    isSchoolShell,
  ]);

  const handleCitationClick = useCallback(
    (citation: TCitation) => {
      if (citationDisplayMode === "inline_sources") {
        return;
      }
      setActiveCitation(citation);
      onCitationClick?.(citation);
    },
    [citationDisplayMode, onCitationClick],
  );

  const closeCitation = useCallback(() => {
    setActiveCitation(null);
  }, []);

  const resolveInputActionPrompt = useCallback(
    (action: AssistantInputAction, value?: string) => {
      const trimmedValue = value?.trim() ?? "";
      const optionLabel =
        action.input?.options?.find((option) => option.value === trimmedValue)
          ?.label ?? "";
      const hasSafeHref =
        Boolean(trimmedValue) &&
        (trimmedValue.startsWith("/") || /^https?:\/\//.test(trimmedValue));
      const template =
        action.promptTemplate ??
        (typeof action.payload?.promptTemplate === "string"
          ? action.payload.promptTemplate
          : undefined);
      if (template) {
        return template
          .replace(/\{\{\s*value\s*\}\}/g, trimmedValue)
          .replace(
            /\{\{\s*label\s*\}\}/g,
            optionLabel || trimmedValue || action.label,
          );
      }
      if (action.type === "navigate") {
        if (trimmedValue && hasSafeHref) {
          return `Navigate to [${optionLabel || trimmedValue}](${trimmedValue}).`;
        }
        if (trimmedValue) {
          return `Navigate to ${optionLabel || trimmedValue}.`;
        }
        return `Navigate to ${action.label}.`;
      }
      if (action.type === "prompt") {
        return trimmedValue || action.label;
      }
      return trimmedValue || action.label;
    },
    [],
  );

  const handleInputActionSubmit = useCallback(() => {
    if (!activeInputAction || conversation.isLoading) {
      return;
    }
    const value = activeInputAction.input?.type
      ? inputActionValue.trim()
      : undefined;
    if (onInputAction) {
      onInputAction(activeInputAction, value);
    } else if (
      activeInputAction.type === "navigate" &&
      activeInputAction.payload &&
      (activeInputAction.payload as { direct?: boolean }).direct &&
      typeof (activeInputAction.payload as { href?: string }).href ===
        "string" &&
      typeof window !== "undefined"
    ) {
      window.location.assign(
        (activeInputAction.payload as { href: string }).href,
      );
    } else {
      const prompt = resolveInputActionPrompt(activeInputAction, value);
      if (prompt) {
        handleSubmit(prompt);
      }
    }
    setActiveInputActionId(null);
    setInputActionValue("");
  }, [
    activeInputAction,
    conversation.isLoading,
    inputActionValue,
    onInputAction,
    resolveInputActionPrompt,
    handleSubmit,
  ]);

  const hasInputActions = actionItems.length > 0;
  const inputActionReady =
    !activeInputAction ||
    !activeInputAction.input?.type ||
    Boolean(inputActionValue.trim());
  const hasTypedQuestion = question.trim().length > 0;
  const canStopActiveResponse = conversation.isLoading;
  const [showContextSummary, setShowContextSummary] = useState(false);
  const canToggleContextSummary = Boolean(contextSummary);
  const schoolShellContextMenuRef = useRef<HTMLDivElement>(null);
  const schoolShellContextButtonRef = useRef<HTMLButtonElement>(null);
  const [schoolShellContextMenuStyle, setSchoolShellContextMenuStyle] =
    useState<CSSProperties>({});

  useEffect(() => {
    if (!isSchoolShell || !showContextSummary) {
      return undefined;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!schoolShellContextMenuRef.current?.contains(event.target as Node)) {
        setShowContextSummary(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isSchoolShell, showContextSummary]);

  useLayoutEffect(() => {
    if (
      !isSchoolShell ||
      !showContextSummary ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const viewportPadding = 12;
    const menuGap = 12;
    const preferredMenuWidth = 280;
    const preferredMenuHeight = 520;

    const updateContextMenuStyle = () => {
      const anchorRect =
        schoolShellContextButtonRef.current?.getBoundingClientRect();
      if (!anchorRect) {
        return;
      }

      const menuWidth = Math.min(
        preferredMenuWidth,
        Math.max(0, window.innerWidth - viewportPadding * 2),
      );
      const left = Math.min(
        Math.max(viewportPadding, anchorRect.right - menuWidth),
        Math.max(
          viewportPadding,
          window.innerWidth - viewportPadding - menuWidth,
        ),
      );
      const availableBelow = Math.max(
        0,
        Math.floor(
          window.innerHeight - anchorRect.bottom - menuGap - viewportPadding,
        ),
      );
      const availableAbove = Math.max(
        0,
        Math.floor(anchorRect.top - menuGap - viewportPadding),
      );
      const opensAbove =
        availableBelow < Math.min(preferredMenuHeight, 240) &&
        availableAbove > availableBelow;
      const availableVerticalSpace = opensAbove
        ? availableAbove
        : availableBelow;
      const constrainedMaxHeight = Math.min(
        preferredMenuHeight,
        availableVerticalSpace,
      );
      const top = opensAbove
        ? Math.max(
            viewportPadding,
            anchorRect.top - menuGap - constrainedMaxHeight,
          )
        : Math.min(
            anchorRect.bottom + menuGap,
            Math.max(
              viewportPadding,
              window.innerHeight - viewportPadding - constrainedMaxHeight,
            ),
          );

      setSchoolShellContextMenuStyle({
        position: "fixed",
        left: `${left}px`,
        right: "auto",
        top: `${top}px`,
        bottom: "auto",
        width: `${menuWidth}px`,
        maxHeight: `${constrainedMaxHeight}px`,
      });
    };

    updateContextMenuStyle();
    window.addEventListener("resize", updateContextMenuStyle);
    window.addEventListener("scroll", updateContextMenuStyle, {
      passive: true,
    });

    return () => {
      window.removeEventListener("resize", updateContextMenuStyle);
      window.removeEventListener("scroll", updateContextMenuStyle);
    };
  }, [isSchoolShell, showContextSummary]);

  useEffect(() => {
    if (!isSchoolShell) {
      return;
    }
    const hasSubmittedTurnPendingReveal =
      pendingSchoolShellTranscriptRevealRef.current;
    if (
      conversation.pendingQuestion ||
      conversation.isLoading ||
      hasSubmittedTurnPendingReveal
    ) {
      setSchoolShellView("chat");
      if (
        hasSubmittedTurnPendingReveal &&
        !conversation.pendingQuestion &&
        !conversation.isLoading &&
        (conversation.messages.length > 0 || Boolean(conversation.errorMessage))
      ) {
        pendingSchoolShellTranscriptRevealRef.current = false;
      }
      return;
    }
    if (
      conversation.messages.length === 0 &&
      schoolShellView === "chat" &&
      !conversationLeadContent &&
      controlledSchoolShellView === undefined
    ) {
      setSchoolShellView(assistantWorkspace?.defaultTab ?? "navigate");
    }
  }, [
    assistantWorkspace?.defaultTab,
    controlledSchoolShellView,
    conversationLeadContent,
    conversation.errorMessage,
    conversation.isLoading,
    conversation.messages.length,
    conversation.pendingQuestion,
    isSchoolShell,
    schoolShellView,
    setSchoolShellView,
  ]);

  const toggleExpanded = () => {
    if (!isOverlay) {
      return;
    }
    panel.toggleExpanded();
    if (!panel.isExpanded) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const HeaderComponent = components?.Header ?? AIAssistantHeader;
  const ContextSummaryComponent =
    components?.ContextSummary ?? AIAssistantContextSummary;
  const MessageComponent = components?.Message ?? AIAssistantMessage;
  const SuggestedPromptsComponent =
    components?.SuggestedPrompts ?? AIAssistantSuggestedPrompts;
  const ThinkingIndicatorComponent =
    components?.ThinkingIndicator ?? AIAssistantThinkingIndicator;
  const hasDashboardPrompts = quickActionItems.length > 0;

  const handleAssistantPrimaryAction = useCallback(() => {
    if (canStopActiveResponse) {
      void conversation.cancelResponse();
      return;
    }
    if (isSchoolShell) {
      submitSchoolShellQuestion(question);
      return;
    }
    handleSubmit(question);
  }, [
    canStopActiveResponse,
    conversation,
    handleSubmit,
    isSchoolShell,
    question,
    submitSchoolShellQuestion,
  ]);
  const schoolShellToolbarActionClass =
    schoolShellAssistantStyleContract.toolbarActionClassName;
  const schoolShellGroundingToggleClass =
    schoolShellAssistantStyleContract.groundingToggleClassName;
  const schoolShellTabButtonClass =
    schoolShellAssistantStyleContract.tabButtonClassName;
  const schoolShellHeaderActionClass =
    schoolShellAssistantStyleContract.headerActionClassName;
  const schoolShellHeaderModelPillClass =
    schoolShellAssistantStyleContract.headerModelPillClassName;
  const activateSchoolShellTab = useCallback(
    (tabId: AIAssistantWorkspaceTabId) => {
      if (tabId === "chat") {
        armRouteStartPointerInterlock();
      }
      setSchoolShellView(tabId);
      setShowAccessorySheet(false);
    },
    [armRouteStartPointerInterlock, setSchoolShellView],
  );
  const shouldShowRouteStartPointerInterlock =
    isSchoolShell &&
    schoolShellView === "chat" &&
    quickActionItems.length > 0 &&
    !conversation.pendingQuestion &&
    !conversation.isLoading &&
    conversation.messages.length === 0;

  useEffect(() => {
    if (!isSchoolShell) {
      routeStartEmptyStateVisibleRef.current = false;
      return;
    }
    if (
      shouldShowRouteStartPointerInterlock &&
      !routeStartEmptyStateVisibleRef.current
    ) {
      armRouteStartPointerInterlock();
    }
    routeStartEmptyStateVisibleRef.current =
      shouldShowRouteStartPointerInterlock;
  }, [
    armRouteStartPointerInterlock,
    isSchoolShell,
    shouldShowRouteStartPointerInterlock,
  ]);

  const schoolShellChromeMode = assistantWorkspace?.chromeMode ?? "default";
  const isPrototypeMinimalChrome =
    schoolShellChromeMode === "prototype-minimal";

  const renderSchoolShellQuickActionCard = (
    prompt: AssistantPrompt,
    options: {
      surface: "chat" | "navigate" | "sticky";
      keyPrefix?: string;
    },
  ) => {
    const Icon = resolveQuickActionIcon(prompt);
    const isRouteStartSurface =
      options.surface === "chat" || options.surface === "sticky";
    const provenance = prompt.provenance;
    const likelySources = joinAttributeList(provenance?.likelySources);
    const routePrefixes = joinAttributeList(provenance?.routePrefixes);
    const promptText = resolveAssistantPromptText(prompt);
    const interactivePrompt: AssistantPrompt = {
      ...prompt,
      interactionMode: isRouteStartSurface
        ? "chat-route-start"
        : "navigate-quick-action",
    };
    const routePrefix = provenance?.routePrefixes?.[0];
    const secondaryCopy = routePrefix
      ? `Reusable route-aware starter for ${routePrefix}.`
      : "Reusable route-aware starter for this workspace.";
    const quickActionCardClass =
      schoolShellAssistantStyleContract.quickActionCardClassName({
        surface: options.surface,
        isPrototypeMinimalChrome,
      });

    return (
      <motion.button
        key={`${options.keyPrefix ?? options.surface}-${prompt.id ?? prompt.label}-${onPromptClick ? "prompt-handler" : "submit-fallback"}`}
        onClick={(event) => handlePromptSelection(interactivePrompt, event)}
        className={quickActionCardClass}
        data-testid="ai-school-shell-quick-action"
        data-ai-prompt-surface={options.surface}
        data-ai-prompt-label={prompt.label}
        data-ai-prompt-prompt={promptText}
        data-ai-prompt-provenance={provenance ? "present" : "missing"}
        data-ai-prompt-capability-id={provenance?.capabilityId}
        data-ai-prompt-authority-page={provenance?.authorityPage ?? undefined}
        data-ai-prompt-capability-type={provenance?.capabilityType ?? undefined}
        data-ai-prompt-source-origin={provenance?.sourceOrigin ?? undefined}
        data-ai-prompt-generation-mode={provenance?.generationMode ?? undefined}
        data-ai-prompt-llm-role={provenance?.llmRole ?? undefined}
        data-ai-prompt-deterministic-handler-id={
          provenance?.deterministicHandlerId ?? undefined
        }
        data-ai-prompt-scenario-key={provenance?.scenarioKey ?? undefined}
        data-ai-prompt-method={provenance?.method ?? undefined}
        data-ai-prompt-likely-sources={likelySources}
        data-ai-prompt-route-prefixes={routePrefixes}
        data-ai-route-start-pointer-interlock={
          isRouteStartSurface && routeStartPointerInterlockActive
            ? "active"
            : "idle"
        }
        type="button"
        style={microTransitionStyle}
        whileHover={subtleLiftHover}
        whileTap={subtlePressMotion}
        transition={getAssistantMotionTransition("micro", reducedMotion)}
      >
        <span
          className={
            schoolShellAssistantStyleContract.quickActionHoverLineClassName
          }
          style={microTransitionStyle}
        />
        {isRouteStartSurface ? (
          <>
            <div className="min-w-0">
              <p
                className={schoolShellAssistantStyleContract.quickActionLabelClassName(
                  isPrototypeMinimalChrome,
                )}
                data-testid="ai-school-shell-quick-action-label"
              >
                {prompt.label}
              </p>
            </div>
            <span
              className={schoolShellAssistantStyleContract.quickActionIconClassName(
                isPrototypeMinimalChrome,
              )}
              data-testid="ai-school-shell-quick-action-icon"
            >
              <Icon size={15} />
            </span>
          </>
        ) : (
          <>
            <span
              className={
                schoolShellAssistantStyleContract.quickActionNavigateIconClassName
              }
              data-testid="ai-school-shell-quick-action-icon"
            >
              <Icon size={17} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/72">
                    Quick action
                  </p>
                  <p
                    className="mt-1 text-[13px] font-semibold leading-5 text-irongrey"
                    data-testid="ai-school-shell-quick-action-label"
                  >
                    {prompt.label}
                  </p>
                </div>
                <span
                  className={
                    schoolShellAssistantStyleContract.quickActionNavigateBadgeClassName
                  }
                >
                  Navigate
                </span>
              </div>
              <p className="mt-1.5 text-[11px] leading-5 text-irongrey/62">
                {secondaryCopy}
              </p>
              {routePrefix ? (
                <p className="mt-2 truncate text-[10px] font-medium uppercase tracking-[0.14em] text-irongrey/44">
                  {routePrefix}
                </p>
              ) : null}
            </div>
          </>
        )}
      </motion.button>
    );
  };
  const schoolShellGreetingCopy =
    strings?.greeting?.trim() ||
    "Good afternoon — 5 new admissions applications arrived this morning.";
  const defaultNavigatePanel = (
    <div className="space-y-8">
      <div className="mt-2">
        <div className="flex items-center justify-between px-1 mb-4">
          <h3 className="text-sm font-semibold text-irongrey">Quick actions</h3>
          <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-irongrey/48">
            Route aware
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {hasDashboardPrompts ? (
            quickActionItems.map((prompt) =>
              renderSchoolShellQuickActionCard(prompt, {
                surface: "navigate",
                keyPrefix: "navigate",
              }),
            )
          ) : (
            <div className="px-1 py-2 text-xs text-irongrey/50">
              No quick actions available.
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-irongrey">
            Recent prompts
          </h3>
        </div>
        <div className="space-y-3">
          {resolvedRecentItems.length > 0 ? (
            resolvedRecentItems.map((item) => {
              const provenance = item.provenance;
              const likelySources = joinAttributeList(
                provenance?.likelySources,
              );
              const routePrefixes = joinAttributeList(
                provenance?.routePrefixes,
              );
              return (
                <motion.button
                  key={item.id}
                  type="button"
                  className="group relative flex w-full items-center gap-4 overflow-hidden rounded-[22px] border border-border bg-surface-light px-4 py-3 text-left text-irongrey hover:border-primary/60 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                  onClick={() => handleRecentItemClick(item)}
                  data-testid="ai-school-shell-recent-item"
                  data-ai-recent-provenance={provenance ? "present" : "missing"}
                  data-ai-recent-capability-id={provenance?.capabilityId}
                  data-ai-recent-source-origin={
                    provenance?.sourceOrigin ?? undefined
                  }
                  data-ai-recent-generation-mode={
                    provenance?.generationMode ?? undefined
                  }
                  data-ai-recent-llm-role={provenance?.llmRole ?? undefined}
                  data-ai-recent-deterministic-handler-id={
                    provenance?.deterministicHandlerId ?? undefined
                  }
                  data-ai-recent-scenario-key={
                    provenance?.scenarioKey ?? undefined
                  }
                  data-ai-recent-method={provenance?.method ?? undefined}
                  data-ai-recent-likely-sources={likelySources}
                  data-ai-recent-route-prefixes={routePrefixes}
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                >
                  <span
                    className="pointer-events-none absolute inset-y-4 left-0 w-1 rounded-full bg-primary/20 opacity-0 group-hover:opacity-100"
                    style={microTransitionStyle}
                  />
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-primary/10 text-primary">
                    <History size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-semibold text-irongrey">
                      {item.title}
                    </h4>
                    <p className="mt-0.5 truncate text-xs text-irongrey/50">
                      {item.subtitle}
                    </p>
                  </div>
                  <span
                    className="rounded-full border border-border bg-background-light px-2.5 py-1 text-[11px] font-semibold text-irongrey/60 group-hover:border-primary/40 group-hover:text-primary"
                    style={microTransitionStyle}
                  >
                    Reuse
                  </span>
                </motion.button>
              );
            })
          ) : (
            <div
              className="px-1 py-1 text-xs text-irongrey/50"
              data-testid="ai-school-shell-recent-empty"
            >
              No recent route-scoped prompts yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
  const defaultHistoryPanel = (
    <div className="space-y-3">
      {resolvedRecentItems.length > 0 ? (
        resolvedRecentItems.map((item) => (
          <motion.button
            key={`history-${item.id}`}
            type="button"
            className="group relative flex w-full items-start gap-4 overflow-hidden rounded-[22px] border border-border bg-surface-light px-4 py-3 text-left text-irongrey hover:border-primary/60 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            onClick={() => handleRecentItemClick(item)}
            data-testid="ai-school-shell-history-item"
            style={microTransitionStyle}
            whileHover={subtleLiftHover}
            whileTap={subtlePressMotion}
            transition={getAssistantMotionTransition("micro", reducedMotion)}
          >
            <span
              className="pointer-events-none absolute inset-y-4 left-0 w-1 rounded-full bg-primary/20 opacity-0 group-hover:opacity-100"
              style={microTransitionStyle}
            />
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-primary/10 text-primary">
              <History size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-irongrey">
                {item.title}
              </p>
              <p className="mt-1 text-xs leading-5 text-irongrey/55">
                {item.subtitle}
              </p>
            </div>
            <span
              className="rounded-full border border-border bg-background-light px-2.5 py-1 text-[11px] font-semibold text-irongrey/60 group-hover:border-primary/40 group-hover:text-primary"
              style={microTransitionStyle}
            >
              Open
            </span>
          </motion.button>
        ))
      ) : (
        <div className="rounded-[20px] border border-dashed border-border bg-background-light px-4 py-5 text-sm text-irongrey/60">
          No assistant history yet. Recent prompts and task updates will appear
          here.
        </div>
      )}
    </div>
  );
  const defaultArtifactsPanel = (
    <div className="rounded-[20px] border border-dashed border-border bg-background-light px-4 py-5 text-sm text-irongrey/60">
      No artifacts are available yet. Generated chart, spreadsheet, and other
      outputs will appear here.
    </div>
  );
  const schoolShellNavigatePanel =
    assistantWorkspace?.navigate?.content ?? defaultNavigatePanel;
  const schoolShellHistoryPanel =
    assistantWorkspace?.history?.content ?? defaultHistoryPanel;
  const schoolShellArtifactsPanel =
    assistantWorkspace?.artifacts?.content ?? defaultArtifactsPanel;
  const legacySchoolShellGroundingContent =
    assistantWorkspace?.groundingSheetContent ?? composerAccessory ?? null;
  const schoolShellWorkspaceView = assistantWorkspace?.workspace ?? null;
  const schoolShellPrimaryActionStyle = {
    ...schoolShellComposerStyleContract.primaryActionStyle({
      isPrototypeMinimalChrome,
    }),
    ...microTransitionStyle,
  } satisfies CSSProperties;
  const schoolShellTranscriptBottomSpacerStyle = {
    flexShrink: 0,
    height: isPrototypeMinimalChrome
      ? "calc(0.75rem + env(safe-area-inset-bottom, 0px) + var(--ai-school-shell-keyboard-inset, 0px))"
      : "calc(1rem + env(safe-area-inset-bottom, 0px) + var(--ai-school-shell-keyboard-inset, 0px))",
  } satisfies CSSProperties;
  const schoolShellHeaderTitle =
    schoolShellView === "workspace"
      ? (schoolShellWorkspaceView?.title ?? "Assistant")
      : "Assistant";
  const schoolShellHeaderHistoryLabel =
    schoolShellView === "workspace" ? "Back" : "Recents";
  const artifactsTabLabel = assistantWorkspace?.artifacts?.label ?? "Artifacts";
  const historyTabLabel = assistantWorkspace?.history?.label ?? "History";
  const navigateTabLabel = assistantWorkspace?.navigate?.label ?? "Navigate";
  const schoolShellTabs = [
    {
      id: "chat" as const,
      label: "Chat",
      icon: Sparkles,
      badgeCount:
        conversation.messages.length > 0
          ? conversation.messages.length
          : undefined,
      visible: true,
    },
    {
      id: "artifacts" as const,
      label: artifactsTabLabel,
      icon: FileText,
      badgeCount: assistantWorkspace?.artifacts?.badgeCount,
      visible: schoolShellArtifactsAvailable,
    },
    {
      id: "history" as const,
      label: historyTabLabel,
      icon: History,
      badgeCount:
        assistantWorkspace?.history?.badgeCount ??
        (resolvedRecentItems.length > 0
          ? resolvedRecentItems.length
          : undefined),
      visible: true,
    },
    {
      id: "navigate" as const,
      label: navigateTabLabel,
      icon: LocateFixed,
      badgeCount:
        assistantWorkspace?.navigate?.badgeCount ??
        (quickActionItems.length > 0 ? quickActionItems.length : undefined),
      visible: true,
    },
  ].filter((tab) => tab.visible);
  const schoolShellBackTarget = schoolShellArtifactsAvailable
    ? "artifacts"
    : (assistantWorkspace?.defaultTab ?? "navigate");
  const showPrototypeMinimalHistoryShortcut =
    !isPrototypeMinimalChrome || schoolShellView !== "chat";
  const shouldShowSchoolShellEmptyChat =
    isSchoolShell && !hasConversation && !conversation.isLoading;
  useEffect(() => {
    if (!shouldShowSchoolShellEmptyChat) {
      setSchoolShellGreetingVisible(false);
      setSchoolShellGreetingDismissed(false);
      return;
    }

    if (reducedMotion) {
      setSchoolShellGreetingVisible(true);
      setSchoolShellGreetingDismissed(false);
      return;
    }

    setSchoolShellGreetingVisible(false);
    setSchoolShellGreetingDismissed(false);
    const showTimer = window.setTimeout(
      () => setSchoolShellGreetingVisible(true),
      180,
    );
    const hideTimer = window.setTimeout(
      () => setSchoolShellGreetingDismissed(true),
      10000,
    );

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, [reducedMotion, shouldShowSchoolShellEmptyChat]);
  const schoolShellShortcutTabs = schoolShellTabs.filter(
    (tab) => tab.id !== "chat",
  );
  const schoolShellComposerChestQuickActions =
    quickActionItems.length > 0 ? (
      <section
        className="space-y-2"
        data-testid="ai-school-shell-composer-chest-quick-actions"
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-irongrey/55">
            Route starters
          </p>
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-irongrey/42">
            {quickActionItems.length} available
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {quickActionItems.map((prompt) =>
            renderSchoolShellQuickActionCard(prompt, {
              surface: "navigate",
              keyPrefix: "composer-chest",
            }),
          )}
        </div>
      </section>
    ) : null;
  const decorateSchoolShellComposerChestSectionContent = useCallback(
    (
      sectionId: AIAssistantComposerChestSection["id"],
      content: React.ReactNode,
    ): React.ReactNode => {
      const sections: React.ReactNode[] = [];
      if (sectionId === "actions" && schoolShellComposerChestQuickActions) {
        sections.push(
          <div key="route-starters" className="space-y-2">
            {schoolShellComposerChestQuickActions}
          </div>,
        );
      }
      if (content) {
        sections.push(
          <div key="content" className="space-y-2">
            {content}
          </div>,
        );
      }
      if (sections.length === 0) {
        return null;
      }
      if (sections.length === 1) {
        return sections[0];
      }
      return <div className="space-y-3">{sections}</div>;
    },
    [schoolShellComposerChestQuickActions],
  );
  const schoolShellComposerChestSections = useMemo<
    AIAssistantComposerChestSection[]
  >(() => {
    if (assistantWorkspace?.composerChest?.sections?.length) {
      return assistantWorkspace.composerChest.sections
        .map((section) => ({
          ...section,
          content: decorateSchoolShellComposerChestSectionContent(
            section.id,
            section.content,
          ),
        }))
        .filter(
          (section) => section.content != null || section.id === "actions",
        );
    }
    return [
      {
        id: "actions",
        title: "Actions",
        content: decorateSchoolShellComposerChestSectionContent(
          "actions",
          legacySchoolShellGroundingContent ?? null,
        ),
      },
    ];
  }, [
    assistantWorkspace?.composerChest?.sections,
    decorateSchoolShellComposerChestSectionContent,
    legacySchoolShellGroundingContent,
  ]);
  const schoolShellCollapsedRailTabIds = useMemo<
    Array<
      Extract<AIAssistantWorkspaceTabId, "artifacts" | "history" | "navigate">
    >
  >(() => {
    const isCollapsedRailTabId = (
      tabId: AIAssistantWorkspaceTabId,
    ): tabId is Extract<
      AIAssistantWorkspaceTabId,
      "artifacts" | "history" | "navigate"
    > => tabId === "artifacts" || tabId === "history" || tabId === "navigate";
    const configuredTabs = assistantWorkspace?.composerChest?.collapsedRailTabs
      ?.length
      ? assistantWorkspace.composerChest.collapsedRailTabs.filter(
          isCollapsedRailTabId,
        )
      : (["artifacts", "history"] as Array<
          Extract<
            AIAssistantWorkspaceTabId,
            "artifacts" | "history" | "navigate"
          >
        >);
    return configuredTabs.filter((tabId) =>
      schoolShellShortcutTabs.some((tab) => tab.id === tabId),
    );
  }, [
    assistantWorkspace?.composerChest?.collapsedRailTabs,
    schoolShellShortcutTabs,
  ]);
  const schoolShellCollapsedRailTabs = schoolShellShortcutTabs.filter((tab) =>
    schoolShellCollapsedRailTabIds.includes(tab.id),
  );
  const schoolShellExpandedOnlyTabs = schoolShellShortcutTabs.filter(
    (tab) => !schoolShellCollapsedRailTabIds.includes(tab.id),
  );
  const schoolShellContextNavTabs = useMemo(
    () => (isPrototypeMinimalChrome ? schoolShellTabs : []),
    [isPrototypeMinimalChrome, schoolShellTabs],
  );
  const schoolShellSheetActionClass = [
    "group relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-[20px] border border-border bg-surface-light px-4 py-3 text-left text-irongrey",
    "hover:border-primary/40 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" ");
  const schoolShellThreadHasActivity =
    hasConversation ||
    Boolean(conversation.pendingQuestion) ||
    conversation.isLoading;
  const showSchoolShellPersistentRouteStarts = !schoolShellThreadHasActivity;
  const schoolShellChatRouteStarts =
    quickActionItems.length > 0 && showSchoolShellPersistentRouteStarts ? (
      <section
        className="px-0.5 pt-1"
        data-testid="ai-school-shell-conversation-route-starts"
        data-ai-route-start-pointer-interlock={
          routeStartPointerInterlockActive ? "active" : "idle"
        }
      >
        <div className="mb-2 flex items-center justify-between">
          <p
            className={
              schoolShellAssistantStyleContract.quickActionsHeadingClassName
            }
          >
            Quick Actions
          </p>
          {isPrototypeMinimalChrome ? (
            <div className="flex items-center gap-1 pr-[12px]">
              <button
                type="button"
                onClick={() => {
                  const rail =
                    typeof document !== "undefined"
                      ? document.getElementById(
                          "ai-prototype-quick-actions-scroll",
                        )
                      : null;
                  rail?.scrollBy({ left: -160, behavior: "smooth" });
                }}
                className={
                  schoolShellAssistantStyleContract.quickActionsScrollButtonClassName
                }
                aria-label="Scroll quick actions left"
                data-testid="ai-prototype-quick-actions-scroll-left"
              >
                <ChevronLeft size={14} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => {
                  const rail =
                    typeof document !== "undefined"
                      ? document.getElementById(
                          "ai-prototype-quick-actions-scroll",
                        )
                      : null;
                  rail?.scrollBy({ left: 160, behavior: "smooth" });
                }}
                className={
                  schoolShellAssistantStyleContract.quickActionsScrollButtonClassName
                }
                aria-label="Scroll quick actions right"
                data-testid="ai-prototype-quick-actions-scroll-right"
              >
                <ChevronRight size={14} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
        {isPrototypeMinimalChrome ? (
          <div
            id="ai-prototype-quick-actions-scroll"
            className="flex gap-2 overflow-x-auto overflow-y-hidden px-1 pb-2 pr-[12px] pt-1"
            style={{ scrollbarWidth: "none" }}
          >
            {quickActionItems.map((prompt) => (
              <div
                key={`chat-scroll-${prompt.id ?? prompt.label}`}
                className="min-w-0"
                style={{ flex: "0 0 8rem" }}
              >
                {renderSchoolShellQuickActionCard(prompt, {
                  surface: "chat",
                  keyPrefix: "chat-scroll",
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {quickActionItems.map((prompt) =>
              renderSchoolShellQuickActionCard(prompt, {
                surface: "chat",
                keyPrefix: "chat",
              }),
            )}
          </div>
        )}
      </section>
    ) : null;
  const showSchoolShellStickyRouteActions =
    isSchoolShell &&
    activeRouteContext?.routeFamily === "run-reports" &&
    quickActionItems.length > 0 &&
    schoolShellThreadHasActivity;
  const schoolShellStickyRouteActions = showSchoolShellStickyRouteActions ? (
    <section
      className="sticky top-0 z-10 mb-2 border-b border-border bg-surface-light/95 px-0.5 pb-2 pt-1 backdrop-blur"
      data-testid="ai-school-shell-route-action-sticky-widget"
      data-ai-transcript-owned="false"
    >
      <div className="mb-1 flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-secondary">
          Route actions
        </p>
      </div>
      <div
        className="flex gap-2 overflow-x-auto overflow-y-hidden pb-1"
        style={{ scrollbarWidth: "none" }}
      >
        {quickActionItems.map((prompt) => (
          <div
            key={`sticky-scroll-${prompt.id ?? prompt.label}`}
            className="min-w-0"
            style={{ flex: "0 0 7.5rem" }}
          >
            {renderSchoolShellQuickActionCard(prompt, {
              surface: "sticky",
              keyPrefix: "sticky-scroll",
            })}
          </div>
        ))}
      </div>
    </section>
  ) : null;
  const schoolShellChatRecentItems =
    resolvedRecentItems.length > 0 ? (
      <section
        className="px-0.5 pt-1"
        data-testid="ai-school-shell-conversation-recent-items"
      >
        <div className="mb-1.5">
          <p className="text-[11px] font-medium text-irongrey/72">Recent</p>
        </div>
        <div className="space-y-0.5">
          {resolvedRecentItems.map((item) => {
            const provenance = item.provenance;
            const likelySources = joinAttributeList(provenance?.likelySources);
            const routePrefixes = joinAttributeList(provenance?.routePrefixes);
            return (
              <motion.button
                key={`chat-recent-${item.id}`}
                type="button"
                className={
                  schoolShellAssistantStyleContract.recentItemClassName
                }
                onClick={() => handleRecentItemClick(item)}
                data-testid="ai-school-shell-recent-item"
                data-ai-recent-provenance={provenance ? "present" : "missing"}
                data-ai-recent-capability-id={provenance?.capabilityId}
                data-ai-recent-source-origin={
                  provenance?.sourceOrigin ?? undefined
                }
                data-ai-recent-generation-mode={
                  provenance?.generationMode ?? undefined
                }
                data-ai-recent-llm-role={provenance?.llmRole ?? undefined}
                data-ai-recent-deterministic-handler-id={
                  provenance?.deterministicHandlerId ?? undefined
                }
                data-ai-recent-scenario-key={
                  provenance?.scenarioKey ?? undefined
                }
                data-ai-recent-method={provenance?.method ?? undefined}
                data-ai-recent-likely-sources={likelySources}
                data-ai-recent-route-prefixes={routePrefixes}
                style={microTransitionStyle}
                whileHover={subtleLiftHover}
                whileTap={subtlePressMotion}
                transition={getAssistantMotionTransition(
                  "micro",
                  reducedMotion,
                )}
              >
                <div className="flex h-5 w-5 shrink-0 items-center justify-center text-primary">
                  <History size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-xs leading-5 text-irongrey">
                    {item.title}
                  </h4>
                  <p
                    className={
                      schoolShellAssistantStyleContract.recentItemSubtitleClassName
                    }
                  >
                    {item.subtitle}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>
    ) : null;

  const prototypeLeadConversationLayout =
    isPrototypeMinimalChrome && Boolean(conversationLeadContent);
  const prototypeMinimalConversationClassName = prototypeLeadConversationLayout
    ? "space-y-2 px-1 pt-0 pb-4"
    : isPrototypeMinimalChrome
      ? "space-y-2 px-3 pt-2 pb-4 md:space-y-5 md:px-4 md:pt-3 md:pb-6"
      : "space-y-5 px-4 pt-3 pb-6";

  const conversationPane = isSchoolShell ? (
    <div
      className={prototypeMinimalConversationClassName}
      data-testid="ai-school-shell-conversation"
    >
      {conversationLeadContent ? (
        <div
          className={styles.nodisplay}
          data-testid="ai-school-shell-conversation-lead"
        >
          {conversationLeadContent}
        </div>
      ) : null}
      {schoolShellChatRouteStarts ? (
        <div data-testid="ai-school-shell-conversation-route-starts-persistent">
          {schoolShellChatRouteStarts}
        </div>
      ) : null}
      {conversation.messages.map((message, index) => (
        <MessageComponent
          key={message.messageId ?? `msg-${index}`}
          message={message}
          feedback={message.feedback}
          onCitationClick={
            citationDisplayMode === "inline_sources"
              ? undefined
              : handleCitationClick
          }
          isPending={
            message.role === "user" &&
            message.content === conversation.pendingQuestion
          }
          allowRetry={!conversation.isLoading}
          onRetry={conversation.handleRetry}
          onFeedback={conversation.handleFeedback}
          onAction={conversation.handleAction}
          onTaskProposalStart={handleTaskProposalStart}
          onTaskProposalAskInChat={handleTaskProposalAskInChat}
          activeRouteContext={activeRouteContext}
          onAssistantPolicyEvent={onAssistantPolicyEvent}
          onOpenArtifactInGoogleSheets={onOpenArtifactInGoogleSheets}
          onRevealProgress={scrollAssistantTranscriptToBottom}
        />
      ))}
      {conversation.isLoading && <ThinkingIndicatorComponent />}
      <div
        ref={bottomRef}
        aria-hidden="true"
        data-testid="ai-school-shell-transcript-bottom-spacer"
        style={schoolShellTranscriptBottomSpacerStyle}
      />
    </div>
  ) : (
    <Stack gap="lg" p="md" className={styles.chatStack}>
      {conversation.messages.map((message, index) => (
        <MessageComponent
          key={message.messageId ?? `msg-${index}`}
          message={message}
          feedback={message.feedback}
          onCitationClick={
            citationDisplayMode === "inline_sources"
              ? undefined
              : handleCitationClick
          }
          isPending={
            message.role === "user" &&
            message.content === conversation.pendingQuestion
          }
          allowRetry={!conversation.isLoading}
          onRetry={conversation.handleRetry}
          onFeedback={conversation.handleFeedback}
          onAction={conversation.handleAction}
          onTaskProposalStart={handleTaskProposalStart}
          onTaskProposalAskInChat={handleTaskProposalAskInChat}
          activeRouteContext={activeRouteContext}
          onAssistantPolicyEvent={onAssistantPolicyEvent}
          onOpenArtifactInGoogleSheets={onOpenArtifactInGoogleSheets}
          onRevealProgress={scrollAssistantTranscriptToBottom}
        />
      ))}
      {conversation.isLoading && <ThinkingIndicatorComponent />}
      <div ref={bottomRef} />
    </Stack>
  );

  const schoolShellEmptyChatPane = isSchoolShell ? (
    <div className="flex min-h-full flex-col gap-4">
      {conversationLeadContent ? (
        <div
          className={styles.nodisplay}
          data-testid="ai-school-shell-conversation-lead"
        >
          {conversationLeadContent}
        </div>
      ) : null}
      {!schoolShellGreetingDismissed ? (
        <motion.section
          className={schoolShellAssistantStyleContract.emptyIntroClassName(
            isPrototypeMinimalChrome,
          )}
          data-testid="ai-school-shell-empty-chat-intro"
          data-state={schoolShellGreetingVisible ? "visible" : "hidden"}
          initial={reducedMotion ? false : { opacity: 0, y: 8 }}
          animate={
            reducedMotion
              ? { opacity: 1 }
              : {
                  opacity: schoolShellGreetingVisible ? 1 : 0,
                  y: schoolShellGreetingVisible ? 0 : 8,
                }
          }
          transition={getAssistantMotionTransition("panel", reducedMotion)}
        >
          <p className="text-xs leading-5 text-irongrey/88">
            {schoolShellGreetingCopy}
          </p>
        </motion.section>
      ) : null}
      {schoolShellChatRouteStarts}
      {schoolShellChatRecentItems}
      {isPrototypeMinimalChrome ? (
        <div
          className="flex flex-1 items-center justify-center pt-6"
          data-testid="ai-school-shell-empty-floor"
        >
          <div className="relative">
            <MessageCircle
              size={40}
              className={
                schoolShellAssistantStyleContract.emptyFloorIconClassName
              }
            />
            <Sparkles
              size={14}
              className={
                schoolShellAssistantStyleContract.emptyFloorSparkleClassName
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  ) : null;
  const schoolShellActivePanelKey =
    schoolShellView === "chat"
      ? shouldShowSchoolShellEmptyChat
        ? "chat-empty"
        : "chat-active"
      : schoolShellView;
  const schoolShellActivePanel =
    schoolShellView === "workspace" ? (
      schoolShellWorkspaceView ? (
        <div
          className="min-h-0 flex-1"
          data-testid="ai-school-shell-workspace-panel"
        >
          {schoolShellWorkspaceView.content}
        </div>
      ) : null
    ) : schoolShellView === "artifacts" && schoolShellArtifactsAvailable ? (
      <div data-testid="ai-school-shell-artifacts-panel">
        {schoolShellArtifactsPanel}
      </div>
    ) : schoolShellView === "history" ? (
      <div data-testid="ai-school-shell-history-panel">
        {schoolShellHistoryPanel}
      </div>
    ) : schoolShellView === "navigate" ? (
      <div data-testid="ai-school-shell-navigate-panel">
        {schoolShellNavigatePanel}
      </div>
    ) : shouldShowSchoolShellEmptyChat ? (
      schoolShellEmptyChatPane
    ) : (
      <>
        {schoolShellStickyRouteActions}
        {conversationPane}
      </>
    );

  const schoolShellComposerChestNode =
    isSchoolShell && !isPrototypeMinimalChrome ? (
      <div
        className={[
          "overflow-hidden rounded-[24px]",
          showAccessorySheet
            ? "bg-surface-light"
            : "bg-transparent shadow-none",
        ].join(" ")}
        data-testid="ai-school-shell-composer-chest"
        data-state={showAccessorySheet ? "expanded" : "collapsed"}
        style={panelTransitionStyle}
      >
        <AnimatePresence initial={false}>
          {showAccessorySheet ? (
            <AssistantMeasuredDisclosure
              key="school-shell-grounding-sheet"
              className="max-h-[45dvh] overflow-y-auto overscroll-contain border-b border-border bg-background-light"
              data-testid="ai-school-shell-grounding-sheet"
            >
              <div className="border-b border-border bg-muted px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
                  Tool chest
                </p>
                <p className="mt-1 text-sm text-irongrey/72">
                  Actions, workspaces, and background tasks stay here so the
                  prompt well remains simple on mobile.
                </p>
              </div>
              <div className="space-y-4 px-4 py-4">
                {schoolShellComposerChestSections.map(
                  (section, sectionIndex) => {
                    const sectionCards: React.ReactNode[] = [];

                    if (section.id === "actions") {
                      sectionCards.push(
                        <motion.button
                          key="attach-source-file"
                          type="button"
                          className={schoolShellSheetActionClass}
                          onClick={() => {
                            triggerAttachInput();
                            setShowAccessorySheet(false);
                          }}
                          disabled={
                            !onAttachFile ||
                            conversation.isLoading ||
                            isAttachingFile
                          }
                          data-testid="ai-school-shell-grounding-attach"
                          style={microTransitionStyle}
                          whileHover={subtleLiftHover}
                          whileTap={subtlePressMotion}
                          transition={getAssistantMotionTransition(
                            "micro",
                            reducedMotion,
                          )}
                        >
                          <span
                            className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent opacity-0 group-hover:opacity-100"
                            style={microTransitionStyle}
                          />
                          <span className="inline-flex min-w-0 items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-surface-light text-primary">
                              <Plus size={18} />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-irongrey">
                                Attach source file
                              </span>
                              <span className="mt-1 block text-xs text-irongrey/62">
                                {onAttachFile
                                  ? isAttachingFile
                                    ? "Uploading file context…"
                                    : "Add a PDF, image, or document for grounding."
                                  : "File attachment is unavailable in this surface."}
                              </span>
                            </span>
                          </span>
                        </motion.button>,
                      );

                      schoolShellExpandedOnlyTabs.forEach((tab) => {
                        const Icon = tab.icon;
                        const isActive = schoolShellView === tab.id;
                        sectionCards.push(
                          <motion.button
                            key={`expanded-only-tab-${tab.id}`}
                            type="button"
                            className={schoolShellSheetActionClass}
                            onClick={() => {
                              activateSchoolShellTab(tab.id);
                              setShowAccessorySheet(false);
                            }}
                            data-testid={`ai-school-shell-grounding-tab-${tab.id}`}
                            aria-pressed={isActive}
                            style={microTransitionStyle}
                            whileHover={subtleLiftHover}
                            whileTap={subtlePressMotion}
                            transition={getAssistantMotionTransition(
                              "micro",
                              reducedMotion,
                            )}
                          >
                            <span
                              className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent opacity-0 group-hover:opacity-100"
                              style={microTransitionStyle}
                            />
                            <span className="inline-flex min-w-0 items-center gap-3">
                              <span
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${isActive ? "border-primary bg-primary text-white" : "border-border bg-surface-light text-primary"}`}
                              >
                                <Icon size={18} />
                              </span>
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-irongrey">
                                  {tab.label}
                                </span>
                                <span className="mt-1 block text-xs text-irongrey/62">
                                  {tab.id === "navigate"
                                    ? "Open route-aware prompts and page actions."
                                    : tab.id === "artifacts"
                                      ? "Open generated outputs and workspaces."
                                      : "Review recent prompts and task updates."}
                                </span>
                              </span>
                            </span>
                            {typeof tab.badgeCount === "number" &&
                            tab.badgeCount > 0 ? (
                              <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                                {tab.badgeCount}
                              </span>
                            ) : null}
                          </motion.button>,
                        );
                      });
                    }

                    if (section.content) {
                      sectionCards.push(
                        <div
                          key={`${section.id}-content`}
                          className="space-y-2"
                        >
                          {section.content}
                        </div>,
                      );
                    }

                    if (sectionCards.length === 0) {
                      return null;
                    }

                    return (
                      <AssistantMeasuredDisclosure
                        key={section.id}
                        className="space-y-2"
                        data-testid={`ai-school-shell-composer-chest-section-${section.id}`}
                        style={{
                          ...panelTransitionStyle,
                          animationDelay: reducedMotion
                            ? undefined
                            : `${sectionIndex * 40}ms`,
                        }}
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-irongrey/55">
                          {section.title}
                        </p>
                        <div className="space-y-2">{sectionCards}</div>
                      </AssistantMeasuredDisclosure>
                    );
                  },
                )}
              </div>
            </AssistantMeasuredDisclosure>
          ) : null}
        </AnimatePresence>
        <div className="px-2.5 py-2" data-testid="ai-school-shell-toolbar">
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
            <motion.button
              type="button"
              className={schoolShellGroundingToggleClass(showAccessorySheet)}
              aria-label={
                showAccessorySheet
                  ? "Collapse tool chest"
                  : "Open grounding and actions"
              }
              aria-expanded={showAccessorySheet}
              data-testid="ai-school-shell-grounding-toggle"
              onClick={() => setShowAccessorySheet((current) => !current)}
              disabled={conversation.isLoading}
              title={
                showAccessorySheet ? "Collapse tool chest" : "Open tool chest"
              }
              style={microTransitionStyle}
              whileHover={subtleLiftHover}
              whileTap={subtlePressMotion}
              transition={getAssistantMotionTransition("micro", reducedMotion)}
            >
              <span
                className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent opacity-0 group-hover:opacity-100"
                style={microTransitionStyle}
              />
              <motion.span
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${showAccessorySheet ? "border-primary/20 bg-primary/10 text-primary" : "border-border bg-muted text-irongrey/65 group-hover:border-primary/40 group-hover:text-primary"}`}
                animate={
                  reducedMotion
                    ? undefined
                    : { rotate: showAccessorySheet ? 45 : 0 }
                }
                transition={{
                  type: "spring",
                  ...resolveAssistantSpring("gesture"),
                }}
              >
                <Plus size={18} />
              </motion.span>
              <span className="flex min-w-0 flex-col items-start pr-1 text-left">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-current/80">
                  Actions
                </span>
                <span className="text-[11px] text-current/65">
                  {showAccessorySheet ? "Collapse chest" : "Open tool chest"}
                </span>
              </span>
              <span
                className="h-5 w-px rounded-full bg-border"
                aria-hidden="true"
              />
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-transparent text-current"
                data-testid="ai-school-shell-grounding-toggle-indicator"
                aria-hidden="true"
              >
                <AssistantAnimatedIcon
                  icon={
                    showAccessorySheet ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )
                  }
                  iconKey={showAccessorySheet ? "expanded" : "collapsed"}
                  className="inline-flex items-center justify-center"
                />
              </span>
            </motion.button>
            {schoolShellCollapsedRailTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = schoolShellView === tab.id;
              return (
                <motion.button
                  key={`toolbar-${tab.id}`}
                  type="button"
                  className={schoolShellToolbarActionClass(isActive)}
                  onClick={() => activateSchoolShellTab(tab.id)}
                  data-testid={`ai-school-shell-toolbar-${tab.id}`}
                  aria-pressed={isActive}
                  aria-label={tab.label}
                  title={tab.label}
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                >
                  <Icon size={22} />
                  <span className="sr-only">{tab.label}</span>
                  {typeof tab.badgeCount === "number" && tab.badgeCount > 0 ? (
                    <span className="absolute -right-1 -top-1 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
                      {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
                    </span>
                  ) : null}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    ) : null;

  const panelClassName = isOverlay
    ? styles.panel
    : [styles.panelSidebar, isSchoolShell ? styles.panelSidebarSchoolShell : ""]
        .filter(Boolean)
        .join(" ");
  const panelInlineStyle = isOverlay
    ? undefined
    : ({
        position: "relative",
        height: "100%",
        minHeight: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      } satisfies CSSProperties);

  const panelContent = (
    <Paper
      className={panelClassName}
      style={panelInlineStyle}
      shadow={isOverlay ? "lg" : undefined}
      radius={isOverlay ? "md" : 0}
      data-testid="ai-assistant"
    >
      <AIAssistantConfigProvider value={{ branding, strings, metaLine }}>
        {isSchoolShell && showSchoolShellHeader ? (
          <div
            className={schoolShellAssistantStyleContract.schoolHeaderClassName}
            data-testid="ai-school-shell-header"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              {schoolShellView === "workspace" ? (
                <motion.button
                  type="button"
                  onClick={() => activateSchoolShellTab(schoolShellBackTarget)}
                  className={
                    schoolShellAssistantStyleContract.schoolBackButtonClassName
                  }
                  aria-label={
                    schoolShellWorkspaceView?.backLabel ??
                    `Back to ${schoolShellBackTarget}`
                  }
                  data-testid="ai-school-shell-back-button"
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                >
                  <ArrowLeft size={20} />
                </motion.button>
              ) : null}
              <h2
                className="truncate text-[16px] font-semibold leading-5 text-irongrey"
                data-testid="ai-school-shell-header-title"
              >
                {schoolShellHeaderTitle}
              </h2>
              {!isPrototypeMinimalChrome &&
              showPrototypeMinimalHistoryShortcut ? (
                <motion.button
                  type="button"
                  className={
                    schoolShellAssistantStyleContract.schoolHistoryShortcutClassName
                  }
                  onClick={() =>
                    activateSchoolShellTab(
                      schoolShellView === "workspace"
                        ? schoolShellBackTarget
                        : "history",
                    )
                  }
                  data-testid="ai-school-shell-history-shortcut"
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                >
                  <History size={14} />
                  <span>{schoolShellHeaderHistoryLabel}</span>
                </motion.button>
              ) : null}
            </div>
            <div
              className="relative flex items-center gap-2"
              ref={schoolShellContextMenuRef}
            >
              {schoolShellModelLabel ? (
                <motion.button
                  type="button"
                  ref={
                    isPrototypeMinimalChrome
                      ? schoolShellContextButtonRef
                      : undefined
                  }
                  className={schoolShellHeaderModelPillClass}
                  onClick={
                    isPrototypeMinimalChrome
                      ? () => setShowContextSummary((prev) => !prev)
                      : undefined
                  }
                  aria-expanded={
                    isPrototypeMinimalChrome ? showContextSummary : undefined
                  }
                  aria-label={
                    isPrototypeMinimalChrome
                      ? `Assistant context and model settings for ${schoolShellModelLabel}`
                      : undefined
                  }
                  data-testid="ai-school-shell-model-pill"
                  style={
                    isPrototypeMinimalChrome ? microTransitionStyle : undefined
                  }
                  whileHover={
                    isPrototypeMinimalChrome ? subtleLiftHover : undefined
                  }
                  whileTap={
                    isPrototypeMinimalChrome ? subtlePressMotion : undefined
                  }
                  transition={
                    isPrototypeMinimalChrome
                      ? getAssistantMotionTransition("micro", reducedMotion)
                      : undefined
                  }
                >
                  <LocateFixed size={14} className="shrink-0 text-primary/80" />
                  <span className="truncate">{schoolShellModelLabel}</span>
                </motion.button>
              ) : null}
              {!isPrototypeMinimalChrome &&
              (hasConversation || conversation.isLoading) ? (
                <motion.button
                  type="button"
                  className={schoolShellHeaderActionClass}
                  aria-label="Start a new session"
                  data-testid="ai-school-shell-new-conversation"
                  data-ai-session-control="new-session"
                  onClick={handleNewConversation}
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                >
                  <AssistantAnimatedIcon
                    icon={<RefreshCcw size={18} />}
                    iconKey="new-session"
                    className="inline-flex items-center justify-center"
                  />
                </motion.button>
              ) : null}
              {isPrototypeMinimalChrome &&
              showPrototypeMinimalHistoryShortcut ? (
                <motion.button
                  type="button"
                  className={schoolShellHeaderActionClass}
                  aria-label="Recent prompts"
                  data-testid="ai-school-shell-history-shortcut"
                  onClick={() =>
                    activateSchoolShellTab(
                      schoolShellView === "workspace"
                        ? schoolShellBackTarget
                        : "history",
                    )
                  }
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                >
                  <AssistantAnimatedIcon
                    icon={<History size={18} />}
                    iconKey="history-shortcut"
                    className="inline-flex items-center justify-center"
                  />
                </motion.button>
              ) : null}
              <div className="relative">
                <motion.button
                  type="button"
                  ref={schoolShellContextButtonRef}
                  className={schoolShellHeaderActionClass}
                  aria-label="Assistant context and model settings"
                  aria-expanded={showContextSummary}
                  data-testid="ai-school-shell-context-button"
                  onClick={() => setShowContextSummary((prev) => !prev)}
                  style={microTransitionStyle}
                  whileHover={subtleLiftHover}
                  whileTap={subtlePressMotion}
                  transition={getAssistantMotionTransition(
                    "micro",
                    reducedMotion,
                  )}
                  hidden={
                    isPrototypeMinimalChrome && Boolean(schoolShellModelLabel)
                  }
                >
                  <AssistantAnimatedIcon
                    icon={<LocateFixed size={20} />}
                    iconKey={
                      showContextSummary ? "context-open" : "context-closed"
                    }
                    className="inline-flex items-center justify-center"
                  />
                </motion.button>
                {showContextSummary ? (
                  <div
                    className={
                      schoolShellAssistantStyleContract.contextMenuClassName
                    }
                    data-testid="ai-school-shell-context-menu"
                    ref={schoolShellContextMenuRef}
                    style={{
                      ...schoolShellContextMenuStyle,
                      ...panelTransitionStyle,
                    }}
                  >
                    <AIAssistantSchoolShellContextMenuContent
                      metaLine={metaLine}
                      headerActions={headerActions}
                      navigationTabs={schoolShellContextNavTabs}
                      currentView={schoolShellView}
                      onActivateTab={activateSchoolShellTab}
                      onDismiss={() => setShowContextSummary(false)}
                    />
                  </div>
                ) : null}
              </div>
              {onRequestClose ? (
                <button
                  type="button"
                  className={
                    schoolShellAssistantStyleContract.closeButtonClassName
                  }
                  aria-label="Close assistant"
                  data-testid="ai-school-shell-close-button"
                  onClick={onRequestClose}
                  style={microTransitionStyle}
                >
                  <X size={20} />
                </button>
              ) : null}
            </div>
          </div>
        ) : !isSchoolShell ? (
          <HeaderComponent
            title={branding.title}
            subtitle={branding.subtitle}
            icon={branding.icon}
            showReset={hasConversation}
            onReset={handleNewConversation}
            headerActions={headerActions}
          />
        ) : null}

        {isSchoolShell && showContextSummary ? (
          <button
            type="button"
            data-passive-click-surface="true"
            className="absolute inset-0 z-40 bg-irongrey/10 backdrop-blur-[1px]"
            aria-label="Dismiss assistant context menu"
            data-testid="ai-assistant-backdrop"
            onClick={() => setShowContextSummary(false)}
          />
        ) : null}

        {!isSchoolShell && canToggleContextSummary ? (
          <div className={styles.contextToggle} data-testid="ai-context-toggle">
            <Button
              variant="subtle"
              size="xs"
              className={styles.contextToggleButton}
              onClick={() => setShowContextSummary((prev) => !prev)}
              aria-label={
                showContextSummary
                  ? "Hide context summary"
                  : "Show context summary"
              }
              data-testid="ai-context-toggle-button"
            >
              {showContextSummary ? "Hide context" : "Show context"}
            </Button>
          </div>
        ) : null}

        {!isSchoolShell && showContextSummary && contextSummary ? (
          <ContextSummaryComponent
            summary={contextSummary}
            isLoading={contextSummary?.isLoading}
          />
        ) : null}

        {!isSchoolShell && metaLine ? (
          <div className={styles.metaLine} data-testid="ai-data-sources">
            <Text size="xs" className={styles.metaLineText}>
              {metaLine}
            </Text>
          </div>
        ) : null}

        {contextSummary?.error ? (
          <div className={styles.errorMessage} data-testid="ai-context-error">
            <Text size="xs">{contextSummary.error}</Text>
            {contextSummary.onRetry ? (
              <Button
                variant="subtle"
                size="xs"
                className={styles.retryButton}
                onClick={contextSummary.onRetry}
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}

        {conversation.errorMessage ? (
          <div className={styles.errorMessage} data-testid="ai-error-message">
            <Text size="xs">{conversation.errorMessage}</Text>
          </div>
        ) : null}

        {isSchoolShell ? (
          <div
            className="relative flex min-h-0 flex-1 flex-col bg-surface-light text-irongrey"
            style={schoolShellViewportStyle}
            data-testid="ai-school-shell-layout"
          >
            {schoolShellView !== "workspace" && !isPrototypeMinimalChrome ? (
              <div
                className="border-b border-border bg-surface-light px-4 py-2"
                data-testid="ai-school-shell-tab-rail"
              >
                <div className="flex flex-wrap gap-1.5">
                  {schoolShellTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = schoolShellView === tab.id;
                    return (
                      <motion.button
                        key={tab.id}
                        type="button"
                        className={schoolShellTabButtonClass(isActive)}
                        onClick={() => activateSchoolShellTab(tab.id)}
                        data-testid={`ai-school-shell-tab-${tab.id}`}
                        aria-current={isActive ? "page" : undefined}
                        style={microTransitionStyle}
                        whileHover={subtleLiftHover}
                        whileTap={subtlePressMotion}
                        transition={getAssistantMotionTransition(
                          "micro",
                          reducedMotion,
                        )}
                      >
                        <Icon size={16} />
                        <span>{tab.label}</span>
                        {typeof tab.badgeCount === "number" &&
                        tab.badgeCount > 0 ? (
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isActive ? "bg-surface-light text-primary" : "bg-primary/10 text-primary"}`}
                          >
                            {tab.badgeCount}
                          </span>
                        ) : null}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div
              ref={schoolShellChatAreaRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain hide-scrollbar"
              data-testid="ai-chat-area"
              onScroll={handleSchoolShellTranscriptScroll}
            >
              <div
                className={`flex w-full flex-col gap-3.5 ${isConstrainedSchoolShell ? "px-2.5 pb-4 pt-3" : "px-4 pb-5 pt-4"}`}
                data-testid="ai-school-shell-active-panel"
              >
                <AnimatePresence initial={false} mode="wait">
                  <motion.div
                    key={schoolShellActivePanelKey}
                    className={
                      schoolShellAssistantStyleContract.contentRailClassName
                    }
                    initial={
                      reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }
                    }
                    animate={
                      reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }
                    }
                    exit={
                      reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }
                    }
                    transition={getAssistantMotionTransition(
                      "panel",
                      reducedMotion,
                    )}
                  >
                    {schoolShellActivePanel}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <div
              className={schoolShellComposerStyleContract.inputAreaClassName(
                isPrototypeMinimalChrome,
              )}
              data-testid="ai-school-shell-input-area"
              style={schoolShellComposerStyleContract.inputAreaStyle(
                isPrototypeMinimalChrome,
              )}
            >
              <div
                className={
                  schoolShellComposerStyleContract.composerShellClassName
                }
                data-testid="ai-school-shell-composer-shell"
              >
                <input
                  ref={attachInputRef}
                  type="file"
                  accept="application/pdf,image/*,.doc,.docx,.txt"
                  className={
                    schoolShellComposerStyleContract.attachInputClassName
                  }
                  onChange={(event) => {
                    void handleAttachInputChange(event);
                  }}
                  data-testid="ai-school-shell-attach-input"
                />
                {inputBanner ? (
                  <div
                    className={schoolShellComposerStyleContract.inputBannerClassName(
                      inputBanner.kind,
                    )}
                    data-testid={
                      inputBanner.kind === "rate-limit"
                        ? "ai-rate-limit-banner"
                        : "ai-input-banner"
                    }
                    data-ai-input-banner-kind={inputBanner.kind ?? "info"}
                  >
                    <div
                      className={
                        schoolShellComposerStyleContract.inputBannerContentClassName
                      }
                    >
                      <div
                        className={
                          schoolShellComposerStyleContract.inputBannerBodyClassName
                        }
                      >
                        <p
                          className={
                            schoolShellComposerStyleContract.inputBannerTitleClassName
                          }
                        >
                          {inputBanner.title}
                        </p>
                        {inputBanner.description ? (
                          <p
                            className={
                              schoolShellComposerStyleContract.inputBannerDescriptionClassName
                            }
                          >
                            {inputBanner.description}
                          </p>
                        ) : null}
                      </div>
                      {inputBanner.primaryActionLabel ||
                      inputBanner.secondaryActionLabel ? (
                        <div
                          className={
                            schoolShellComposerStyleContract.inputBannerActionsClassName
                          }
                        >
                          {inputBanner.secondaryActionLabel ? (
                            <button
                              type="button"
                              className={
                                schoolShellComposerStyleContract.inputBannerSecondaryActionClassName
                              }
                              onClick={inputBanner.onSecondaryAction}
                            >
                              {inputBanner.secondaryActionLabel}
                            </button>
                          ) : null}
                          {inputBanner.primaryActionLabel ? (
                            <button
                              type="button"
                              className={
                                schoolShellComposerStyleContract.inputBannerPrimaryActionClassName
                              }
                              onClick={inputBanner.onPrimaryAction}
                            >
                              {inputBanner.primaryActionLabel}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {resolvedAttachmentChips.length > 0 ? (
                  <div
                    className={
                      schoolShellComposerStyleContract.fileUploadChipRowClassName
                    }
                    data-testid="ai-file-upload-chip-row"
                  >
                    {resolvedAttachmentChips.map((attachment) => (
                      <div
                        key={attachment.id}
                        className={schoolShellComposerStyleContract.fileUploadChipClassName(
                          attachment.tone,
                        )}
                        data-testid="ai-file-upload-chip"
                        data-ai-upload-tone={attachment.tone ?? "neutral"}
                      >
                        <span
                          className={
                            schoolShellComposerStyleContract.fileUploadChipLabelClassName
                          }
                        >
                          {attachment.label}
                        </span>
                        {attachment.detail ? (
                          <span
                            className={
                              schoolShellComposerStyleContract.fileUploadChipDetailClassName
                            }
                          >
                            {attachment.detail}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                {isPrototypeMinimalChrome && composerAccessory ? (
                  <div
                    className={
                      schoolShellComposerStyleContract.composerAccessoryClassName
                    }
                    data-testid="ai-school-shell-composer-accessory"
                  >
                    {composerAccessory}
                  </div>
                ) : null}
                {schoolShellComposerChestNode}
                {isPrototypeMinimalChrome &&
                stagedFileAttachments.length > 0 ? (
                  <div
                    className={
                      schoolShellComposerStyleContract.stagedAttachmentRowClassName
                    }
                    data-testid="ai-school-shell-attachment-chips"
                  >
                    {stagedFileAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className={
                          schoolShellComposerStyleContract.stagedAttachmentChipClassName
                        }
                        data-testid="ai-school-shell-attachment-chip"
                      >
                        <FileText
                          size={12}
                          className={
                            schoolShellComposerStyleContract.stagedAttachmentIconClassName
                          }
                        />
                        <span
                          className={
                            schoolShellComposerStyleContract.stagedAttachmentLabelClassName
                          }
                        >
                          {attachment.fileName}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeStagedAttachment(attachment.id)}
                          className={
                            schoolShellComposerStyleContract.stagedAttachmentRemoveClassName
                          }
                          aria-label={`Remove ${attachment.fileName}`}
                          data-testid="ai-school-shell-attachment-remove"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div
                  className={schoolShellComposerStyleContract.controlRowClassName(
                    isPrototypeMinimalChrome,
                  )}
                >
                  <div
                    className={schoolShellComposerStyleContract.inputWellClassName(
                      isPrototypeMinimalChrome,
                    )}
                    data-testid="ai-school-shell-input-well"
                    style={schoolShellComposerStyleContract.inputWellStyle({
                      hasTypedQuestion,
                      isPrototypeMinimalChrome,
                      panelTransitionStyle,
                    })}
                  >
                    {isPrototypeMinimalChrome ? (
                      <motion.button
                        type="button"
                        onClick={triggerAttachInput}
                        disabled={
                          !onAttachFile ||
                          conversation.isLoading ||
                          isAttachingFile ||
                          isInputLocked
                        }
                        className={
                          schoolShellComposerStyleContract.groundingAttachClassName
                        }
                        data-testid="ai-school-shell-grounding-attach"
                        style={microTransitionStyle}
                        whileHover={subtleLiftHover}
                        whileTap={subtlePressMotion}
                        transition={getAssistantMotionTransition(
                          "micro",
                          reducedMotion,
                        )}
                      >
                        <Plus size={16} />
                      </motion.button>
                    ) : null}
                    <Textarea
                      ref={textareaRef}
                      className={schoolShellComposerStyleContract.inputClassName(
                        isPrototypeMinimalChrome,
                      )}
                      placeholder={
                        isInputLocked
                          ? "Message limit reached"
                          : isPrototypeMinimalChrome
                            ? (strings?.inputPlaceholder ?? "Ask anything...")
                            : (strings?.inputPlaceholder ??
                              "Ask about grade entry, approvals, summaries, navigation, or artifact generation…")
                      }
                      value={question}
                      onChange={(event) => setQuestion(event.target.value)}
                      onKeyDown={handleTextareaKeyDown}
                      onCompositionStart={() => setIsComposingInput(true)}
                      onCompositionEnd={() => setIsComposingInput(false)}
                      autosize
                      minRows={isPrototypeMinimalChrome ? 1 : 2}
                      maxRows={8}
                      disabled={conversation.isLoading || isInputLocked}
                      data-testid="ai-input"
                      style={schoolShellComposerStyleContract.inputStyle(
                        isPrototypeMinimalChrome,
                        hasTypedQuestion,
                      )}
                    />
                    <motion.button
                      type="button"
                      onClick={handleAssistantPrimaryAction}
                      disabled={
                        isInputLocked ||
                        (!canStopActiveResponse && !hasTypedQuestion)
                      }
                      className={schoolShellComposerStyleContract.primaryActionClassName(
                        {
                          canStopActiveResponse,
                          isPrototypeMinimalChrome,
                        },
                      )}
                      data-testid="ai-school-shell-primary-action"
                      aria-label={
                        canStopActiveResponse
                          ? "Stop assistant response"
                          : "Send message"
                      }
                      data-state={canStopActiveResponse ? "stop" : "send"}
                      style={schoolShellPrimaryActionStyle}
                      whileHover={subtleLiftHover}
                      whileTap={subtlePressMotion}
                      transition={getAssistantMotionTransition(
                        "micro",
                        reducedMotion,
                      )}
                    >
                      <AssistantAnimatedIcon
                        icon={
                          canStopActiveResponse ? (
                            <Square size={16} fill="currentColor" />
                          ) : (
                            <ArrowUp size={16} />
                          )
                        }
                        iconKey={canStopActiveResponse ? "stop" : "send"}
                        className={
                          schoolShellComposerStyleContract.assistantAnimatedIconClassName
                        }
                      />
                    </motion.button>
                  </div>
                </div>
                {isPrototypeMinimalChrome ? (
                  <div
                    className={
                      schoolShellComposerStyleContract.prototypeMinimalDisclaimerClassName
                    }
                    data-testid="ai-school-shell-prototype-minimal-disclaimer"
                  >
                    AI-generated responses. Please check for accuracy.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <>
            <ScrollArea className={styles.chatArea} data-testid="ai-chat-area">
              {!hasConversation && !conversation.isLoading ? (
                <Stack gap="md" className={styles.greeting}>
                  {strings?.greeting ? (
                    <div className={styles.greetingBubble}>
                      <Text size="sm">{strings.greeting}</Text>
                    </div>
                  ) : null}
                  {promptItems.length > 0 ? (
                    <SuggestedPromptsComponent
                      prompts={promptItems}
                      title={resolvedSuggestedTitle}
                      disabled={conversation.isLoading}
                      onPromptClick={handlePromptSelection}
                    />
                  ) : null}
                </Stack>
              ) : (
                conversationPane
              )}
            </ScrollArea>

            <div className={styles.inputArea}>
              {hasInputActions ? (
                <div
                  className={styles.inputActions}
                  data-testid="ai-input-actions"
                >
                  <Text className={styles.inputActionsTitle}>
                    {resolvedInputActionsTitle}
                  </Text>
                  <Group gap="xs" className={styles.inputActionButtons}>
                    {actionItems.map((action) => (
                      <Button
                        key={action.id}
                        size="xs"
                        variant={
                          action.id === activeInputActionId ? "filled" : "light"
                        }
                        onClick={() => setActiveInputActionId(action.id)}
                        disabled={conversation.isLoading}
                        className={styles.inputActionButton}
                        data-testid="ai-input-action-button"
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Group>
                  {activeInputAction ? (
                    <div
                      className={styles.inputActionPanel}
                      data-testid="ai-input-action-panel"
                    >
                      <div className={styles.inputActionPanelHeader}>
                        <div>
                          <Text className={styles.inputActionPanelTitle}>
                            {activeInputAction.label}
                          </Text>
                          {activeInputAction.description ? (
                            <Text size="xs" c="dimmed">
                              {activeInputAction.description}
                            </Text>
                          ) : null}
                        </div>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() => setActiveInputActionId(null)}
                          data-testid="ai-input-action-close"
                          aria-label="Close action"
                        >
                          <X size={14} />
                        </ActionIcon>
                      </div>
                      {activeInputAction.input?.type === "select" ? (
                        <Select
                          data={activeInputAction.input?.options ?? []}
                          value={inputActionValue}
                          onChange={(value) => setInputActionValue(value ?? "")}
                          placeholder={activeInputAction.input?.placeholder}
                          size="sm"
                          className={styles.inputActionInput}
                          data-testid="ai-input-action-select"
                        />
                      ) : null}
                      {activeInputAction.input?.type === "text" ? (
                        <TextInput
                          value={inputActionValue}
                          onChange={(event) =>
                            setInputActionValue(event.currentTarget.value)
                          }
                          placeholder={activeInputAction.input?.placeholder}
                          size="sm"
                          className={styles.inputActionInput}
                          data-testid="ai-input-action-text"
                        />
                      ) : null}
                      <Button
                        size="sm"
                        onClick={handleInputActionSubmit}
                        disabled={!inputActionReady}
                        className={styles.inputActionSubmit}
                        data-testid="ai-input-action-submit"
                      >
                        {activeInputAction.actionLabel ?? "Use action"}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {composerAccessory ? (
                <div
                  className={styles.composerAccessory}
                  data-testid="ai-composer-accessory"
                >
                  {composerAccessory}
                </div>
              ) : null}
              <div className={styles.inputWrapper}>
                <Textarea
                  ref={textareaRef}
                  className={styles.input}
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={handleTextareaKeyDown}
                  onCompositionStart={() => setIsComposingInput(true)}
                  onCompositionEnd={() => setIsComposingInput(false)}
                  placeholder={strings?.inputPlaceholder}
                  minRows={1}
                  maxRows={12}
                  autosize
                  disabled={conversation.isLoading}
                  data-testid="ai-input"
                />
                <ActionIcon
                  className={styles.sendButton}
                  onClick={handleAssistantPrimaryAction}
                  disabled={!canStopActiveResponse && !hasTypedQuestion}
                  size="md"
                  radius="md"
                  variant="filled"
                  data-testid="ai-send-button"
                  aria-label={
                    canStopActiveResponse
                      ? "Stop assistant response"
                      : "Send message"
                  }
                >
                  {canStopActiveResponse ? (
                    <Square size={14} fill="currentColor" />
                  ) : (
                    <ArrowUp size={16} />
                  )}
                </ActionIcon>
              </div>
              <Text className={styles.disclaimer}>
                {strings?.disclaimer ??
                  "AI-generated responses. Please check for accuracy."}
              </Text>
            </div>
          </>
        )}
      </AIAssistantConfigProvider>
    </Paper>
  );

  return (
    <DesignSystemBoundary>
      <div
        className={`${styles.root} ${isOverlay ? styles.container : styles.containerSidebar} ${isSchoolShell ? styles.rootSchoolShell : ""} ${className ?? ""}`}
        data-testid="ai-overlay"
      >
        {isOverlay && (
          <motion.div
            style={microTransitionStyle}
            whileHover={subtleLiftHover}
            whileTap={subtlePressMotion}
            transition={getAssistantMotionTransition("micro", reducedMotion)}
          >
            <ActionIcon
              className={styles.toggleButton}
              onClick={toggleExpanded}
              size="xl"
              radius="xl"
              variant="filled"
              color="dark"
              data-testid="ai-overlay-toggle"
              aria-label={
                panel.isExpanded ? "Close AI Assistant" : "Open AI Assistant"
              }
            >
              <AssistantAnimatedIcon
                icon={
                  panel.isExpanded ? <X size={24} /> : <Sparkles size={24} />
                }
                iconKey={panel.isExpanded ? "overlay-close" : "overlay-open"}
                className="inline-flex items-center justify-center"
              />
            </ActionIcon>
          </motion.div>
        )}

        {isOverlay ? (
          <AnimatePresence initial={false}>
            {panel.isExpanded ? (
              <motion.div
                key="assistant-overlay-panel"
                initial={
                  reducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: 18, scale: 0.98 }
                }
                animate={
                  reducedMotion
                    ? { opacity: 1 }
                    : { opacity: 1, x: 0, scale: 1 }
                }
                exit={
                  reducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: 18, scale: 0.98 }
                }
                transition={getAssistantMotionTransition(
                  "shell",
                  reducedMotion,
                )}
              >
                {panelContent}
              </motion.div>
            ) : null}
          </AnimatePresence>
        ) : (
          panelContent
        )}

        {toastNotification ? (
          <div
            className={`fixed right-6 top-20 z-[220] rounded-full border px-3 py-1.5 text-[11px] font-semibold shadow-soft ${toastToneClass}`}
            data-testid="ai-toast-notification"
            data-ai-toast-tone={toastNotification.tone ?? "success"}
            role="status"
          >
            {toastNotification.message}
          </div>
        ) : null}

        {citationDisplayMode !== "inline_sources" && activeCitation ? (
          <div
            data-passive-click-surface="true"
            className={styles.citationOverlay}
            data-testid="ai-citation-overlay"
            onClick={closeCitation}
          >
            <div
              className={styles.citationCard}
              onClick={(event) => event.stopPropagation()}
            >
              <div className={styles.citationCardHeader}>
                <Text size="xs" className={styles.citationCardTitle}>
                  {citationTitle}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={closeCitation}
                  aria-label="Close citation"
                >
                  <X size={14} />
                </ActionIcon>
              </div>
              <div className={styles.citationCardBody}>
                {citationRenderer ? (
                  citationRenderer(activeCitation)
                ) : (
                  <pre className={styles.citationCardCode}>
                    {JSON.stringify(activeCitation, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DesignSystemBoundary>
  );
}
